import type { TranslationKeys } from '@/lib/i18n/en';

export interface IntegrationData {
  slug: string;
  name: string;
  category: 'training' | 'crm' | 'ecommerce' | 'it' | 'automation';
  description: string;
  longDescription: string;
  icon: string; // Emoji fallback
  /**
   * Logo to show instead of the emoji `icon`.
   *
   * Prefer a local path under /public. Simple Icons dropped several brands on
   * trademark grounds (Slack, Salesforce, Twilio, Freshdesk, Monday, Teams,
   * Outlook, webhooks) and their CDN now 404s those slugs — which is where the
   * audit's thirty broken external images came from. Anything still pointing at
   * cdn.simpleicons.org is a brand that CDN still serves; when one disappears,
   * host the asset rather than swapping in another remote URL.
   */
  logoUrl?: string;
  color: string;
  features: {
    title: string;
    description: string;
  }[];
  syncCapabilities: {
    name: string;
    description: string;
  }[];
  useCases: string[];
  setupSteps: string[];
  // Additional technical details from Airbyte docs
  authMethods?: string[];
  syncModes?: {
    fullRefresh: boolean;
    incremental: boolean;
  };
  requirements?: string[];
  notes?: string[];
}

export interface FAQ {
  question: string;
  answer: string;
}

/**
 * The reader-facing subset of `IntegrationData`, all optional.
 *
 * Arabic records live in `src/lib/integrations/ar.ts` and are spread over the
 * English record, so a missing or half-authored record degrades field by field
 * to English rather than rendering blanks. `slug`, `name`, `category`, `icon`,
 * `logoUrl`, `color` and `syncModes` are deliberately absent — vendor names and
 * presentation config stay canonical.
 */
export type IntegrationDataAr = Partial<
  Pick<
    IntegrationData,
    | 'description'
    | 'longDescription'
    | 'features'
    | 'syncCapabilities'
    | 'useCases'
    | 'setupSteps'
    | 'requirements'
    | 'notes'
  >
>;

// Helper function to get text with translation fallback
function t(
  translations: TranslationKeys['integrations'] | undefined,
  key: string,
): string {
  if (!translations) return key;
  return (translations as Record<string, string>)[key] || key;
}

// Generate all integrations with optional translations
export function getAllIntegrations(
  translations?: TranslationKeys['integrations'],
): IntegrationData[] {
  const tt = (key: string) => t(translations, key);

  return [
    // Notion
    {
      slug: 'notion',
      name: 'Notion',
      category: 'training' as const,
      description: tt('Sync pages, databases, and workspaces'),
      longDescription: tt(
        'Connect your Notion workspace to train your AI agent on your company knowledge. Open syncs your pages, databases, and all content blocks to give your AI agent deep understanding of your documentation, processes, and internal knowledge.',
      ),
      icon: '📝',
      logoUrl: 'https://cdn.simpleicons.org/notion/000000',
      color: '#000000',
      features: [
        {
          title: tt('Full Page Sync'),
          description: tt(
            'Automatically sync all pages from your Notion workspace, including nested subpages and their full content hierarchy.',
          ),
        },
        {
          title: tt('Database Integration'),
          description: tt(
            'Import structured data from Notion databases, including all properties, relations, and rollups for comprehensive knowledge.',
          ),
        },
        {
          title: tt('Real-time Updates'),
          description: tt(
            "Changes in Notion are automatically reflected in your AI agent's knowledge base with configurable sync intervals.",
          ),
        },
        {
          title: tt('Rich Content Support'),
          description: tt(
            'Handles all Notion block types including text, images, code blocks, tables, callouts, and embedded content.',
          ),
        },
      ],
      syncCapabilities: [
        {
          name: tt('Pages'),
          description: tt(
            'All pages and subpages with full content, including title, content blocks, and metadata.',
          ),
        },
        {
          name: tt('Databases'),
          description: tt(
            'Database entries with all properties (text, numbers, dates, selects, multi-selects, relations, formulas).',
          ),
        },
        {
          name: tt('Blocks'),
          description: tt(
            'Individual content blocks including paragraphs, headings, lists, toggles, callouts, code, and more.',
          ),
        },
        {
          name: tt('Users'),
          description: tt(
            'Workspace user information for context on content ownership and permissions.',
          ),
        },
        {
          name: tt('Comments'),
          description: tt(
            'Page and block comments for additional context and discussions.',
          ),
        },
      ],
      useCases: [
        tt('Train AI on internal documentation and SOPs'),
        tt('Sync product knowledge bases for customer support'),
        tt('Import HR policies and employee handbooks'),
        tt('Connect engineering wikis and technical docs'),
        tt('Sync sales playbooks and objection handling guides'),
        tt('Import customer success runbooks'),
      ],
      setupSteps: [
        tt('Log in to your Notion workspace and go to My Integrations page'),
        tt('Click "New integration" and select your workspace'),
        tt(
          'Enable "Read content" capability (required), plus "Read comments" and "Read user information" if needed',
        ),
        tt(
          'Navigate to pages you want to sync and click ••• menu → Add connections → select your integration',
        ),
        tt('Copy the Access Token from the Secrets tab of your integration'),
        tt(
          'In Open, go to AI Training → Sources → Connect Notion and paste your token',
        ),
      ],
      authMethods: ['OAuth 2.0 (recommended)', 'Access Token'],
      syncModes: {
        fullRefresh: true,
        incremental: true,
      },
      requirements: [
        tt('Access to a Notion workspace'),
        tt('Owner permissions to create integrations'),
        tt('Pages must be explicitly shared with the integration'),
      ],
      notes: [
        tt('OAuth 2.0 simplifies setup and is recommended for most users'),
        tt(
          'With Access Token auth, you must manually share each page you want to sync',
        ),
        tt(
          'Incremental sync only replicates data generated after your configured start date',
        ),
        tt('Default start date is 2 years ago if not specified'),
      ],
    },
    // Confluence
    {
      slug: 'confluence',
      name: 'Confluence',
      category: 'training' as const,
      description: tt(
        'Import spaces, pages, and documentation from Confluence',
      ),
      longDescription: tt(
        "Connect your Atlassian Confluence to train your AI agent on your team's documentation. Open syncs spaces, pages, blog posts, and attachments to give your AI agent comprehensive knowledge of your internal documentation.",
      ),
      icon: '📘',
      logoUrl: 'https://cdn.simpleicons.org/confluence/172B4D',
      color: '#0052CC',
      features: [
        {
          title: tt('Space Sync'),
          description: tt(
            'Sync entire Confluence spaces including all pages, blog posts, and nested content hierarchies.',
          ),
        },
        {
          title: tt('Attachment Support'),
          description: tt(
            'Extract and process content from PDF, Word, and other document attachments.',
          ),
        },
        {
          title: tt('Label Filtering'),
          description: tt(
            'Filter content by labels to sync only relevant documentation for your AI agent.',
          ),
        },
        {
          title: tt('Version History'),
          description: tt(
            'Access page version history for understanding content evolution and updates.',
          ),
        },
      ],
      syncCapabilities: [
        {
          name: tt('Pages'),
          description: tt(
            'All Confluence pages with full content, titles, and hierarchical structure via the Confluence REST API.',
          ),
        },
        {
          name: tt('Spaces'),
          description: tt(
            'Space metadata and configuration. Note: The Space stream requires a Confluence Standard or Premium plan.',
          ),
        },
      ],
      useCases: [
        tt('Train AI on technical documentation'),
        tt('Sync product requirements and specs'),
        tt('Import runbooks and incident procedures'),
        tt('Connect team wikis and knowledge bases'),
        tt('Sync onboarding documentation'),
        tt('Import compliance and policy documents'),
      ],
      setupSteps: [
        tt(
          'Go to your Atlassian account settings and navigate to Security → API tokens',
        ),
        tt('Click "Create API token" and give it a descriptive name'),
        tt("Copy the generated API token (you won't see it again)"),
        tt('In Open, go to AI Training → Sources → Connect Confluence'),
        tt(
          'Enter your Confluence domain name (e.g., your-company.atlassian.net)',
        ),
        tt('Enter your Confluence login email and the API token'),
      ],
      authMethods: ['API Token'],
      syncModes: {
        fullRefresh: true,
        incremental: false,
      },
      requirements: [
        tt('Atlassian API Token'),
        tt('Your Confluence domain name'),
        tt('Your Confluence login email'),
        tt('Standard or Premium plan for Space stream'),
      ],
      notes: [
        tt('Only Full Refresh sync is supported (no incremental)'),
        tt('The Space stream requires a Confluence Standard or Premium plan'),
        tt(
          'The connector should not run into API limitations under normal usage',
        ),
        tt('Uses Confluence REST API v2 for data extraction'),
      ],
    },
    // Google Docs
    {
      slug: 'google-docs',
      name: 'Google Docs',
      category: 'training' as const,
      description: tt(
        'Connect documents and shared drives from Google Workspace',
      ),
      longDescription: tt(
        'Connect your Google Workspace to train your AI agent on your documents, spreadsheets, and presentations. Open syncs content from Google Drive, including shared drives, to build comprehensive knowledge.',
      ),
      icon: '📄',
      logoUrl: 'https://cdn.simpleicons.org/googledocs/4285F4',
      color: '#4285F4',
      features: [
        {
          title: tt('Drive Integration'),
          description: tt(
            'Sync documents from personal drives and shared team drives with folder selection.',
          ),
        },
        {
          title: tt('Multi-format Support'),
          description: tt(
            'Process Google Docs, Sheets, Slides, and uploaded files (PDF, Word, etc.).',
          ),
        },
        {
          title: tt('Folder Filtering'),
          description: tt(
            'Select specific folders to sync, keeping your AI focused on relevant content.',
          ),
        },
        {
          title: tt('Sharing Permissions'),
          description: tt(
            'Respect existing sharing permissions to maintain content security.',
          ),
        },
      ],
      syncCapabilities: [
        {
          name: tt('CSV Files'),
          description: tt(
            'Structured data from CSV files with configurable parsing options and schema inference.',
          ),
        },
        {
          name: tt('JSON/JSONL Files'),
          description: tt(
            'JSON and JSON Lines files with full content extraction and nested structure support.',
          ),
        },
        {
          name: tt('Parquet/Avro Files'),
          description: tt(
            'Columnar data formats like Parquet and Avro for efficient structured data sync.',
          ),
        },
        {
          name: tt('Document Files'),
          description: tt(
            'Google Docs and PDF documents with text extraction (experimental document file type format).',
          ),
        },
        {
          name: tt('Excel Files'),
          description: tt(
            'Microsoft Excel spreadsheets with sheet selection and data extraction.',
          ),
        },
      ],
      useCases: [
        tt('Train AI on policy documents and guidelines'),
        tt('Sync meeting notes and decision logs'),
        tt('Import training materials and guides'),
        tt('Connect project documentation'),
        tt('Sync customer communication templates'),
        tt('Import sales collateral and presentations'),
      ],
      setupSteps: [
        tt('In Open, go to AI Training → Sources → Connect Google Drive'),
        tt(
          'Click "Authenticate via Google (OAuth)" and complete the authorization',
        ),
        tt(
          'Copy the folder link from Google Drive (navigate to folder → copy URL)',
        ),
        tt(
          'Paste the folder link - all files in this folder and subfolders will be synced',
        ),
        tt(
          'Use glob patterns to filter files (e.g., "**/*.csv" for only CSV files)',
        ),
        tt('Configure sync frequency and start syncing'),
      ],
      authMethods: ['OAuth (recommended)', 'Service Account Key'],
      syncModes: {
        fullRefresh: true,
        incremental: true,
      },
      requirements: [
        tt('Google Drive folder link'),
        tt('Google Workspace user with access to the folder'),
        tt('For Service Account: JSON key file with proper permissions'),
      ],
      notes: [
        tt('Syncs from a single folder - subfolders are included recursively'),
        tt('Use glob patterns like "**/*.csv" to filter by file type'),
        tt(
          'Document file type format is experimental for PDFs and Google Docs',
        ),
        tt('If folder is not public, grant Service Account access to it'),
        tt(
          'Supports CSV, JSON, JSONL, Parquet, Avro, Excel, and document formats',
        ),
      ],
    },
    // Zendesk Help Center
    {
      slug: 'zendesk-help-center',
      name: 'Zendesk Help Center',
      category: 'training' as const,
      description: tt('Sync articles and knowledge base content from Zendesk'),
      longDescription: tt(
        'Connect your Zendesk Help Center to train your AI agent on your existing knowledge base. Open syncs all articles, categories, and sections to ensure your AI agent has the same knowledge as your help center.',
      ),
      icon: '📚',
      color: '#03363D',
      features: [
        {
          title: tt('Article Sync'),
          description: tt(
            'Automatically sync all published articles with full content and formatting.',
          ),
        },
        {
          title: tt('Category Structure'),
          description: tt(
            'Maintain the category and section hierarchy for organized knowledge.',
          ),
        },
        {
          title: tt('Multi-language'),
          description: tt(
            'Sync articles across all configured languages in your Help Center.',
          ),
        },
        {
          title: tt('Draft Support'),
          description: tt(
            'Optionally include draft articles to prepare AI for upcoming content.',
          ),
        },
      ],
      syncCapabilities: [
        {
          name: tt('Articles'),
          description: tt(
            'Help Center articles with full content, titles, body, labels, and metadata via the Zendesk Help Center API.',
          ),
        },
        {
          name: tt('Article Comments'),
          description: tt(
            'Comments on articles including user feedback and discussions.',
          ),
        },
        {
          name: tt('Categories'),
          description: tt(
            'Help Center categories with names, descriptions, and hierarchical structure.',
          ),
        },
        {
          name: tt('Sections'),
          description: tt(
            'Sections within categories for organized content structure.',
          ),
        },
        {
          name: tt('Posts'),
          description: tt(
            'Community posts from your Zendesk community forums.',
          ),
        },
        {
          name: tt('Topics'),
          description: tt('Community topics and discussion threads.'),
        },
      ],
      useCases: [
        tt('Ensure AI answers match your help center'),
        tt('Train AI on product documentation'),
        tt('Sync troubleshooting guides'),
        tt('Import FAQ content'),
        tt('Connect how-to articles'),
        tt('Sync getting started guides'),
      ],
      setupSteps: [
        tt(
          'In Zendesk Admin Center, go to Apps and Integrations → APIs → Zendesk API',
        ),
        tt('Click "Add API token" under Settings and give it a description'),
        tt("Copy the token immediately (you won't see it again)"),
        tt('In Open, go to AI Training → Sources → Connect Zendesk'),
        tt('Enter your subdomain (from https://YOUR_SUBDOMAIN.zendesk.com/)'),
        tt('Choose OAuth 2.0 or API Token auth, then enter credentials'),
      ],
      authMethods: ['OAuth 2.0 (recommended)', 'API Token'],
      syncModes: {
        fullRefresh: true,
        incremental: true,
      },
      requirements: [
        tt('Zendesk subdomain'),
        tt('API token or OAuth credentials'),
        tt('Email associated with Zendesk account (for API token)'),
      ],
      notes: [
        tt('OAuth is recommended as it simplifies setup significantly'),
        tt('Most streams support incremental sync for efficient updates'),
        tt('Some streams like Audit Logs require enterprise accounts'),
        tt('Two sync types: server-side incremental and client-side filtering'),
        tt(
          'Syncs Help Center articles, categories, sections, and community content',
        ),
      ],
    },
    // GitHub
    {
      slug: 'github',
      name: 'GitHub',
      category: 'training' as const,
      description: tt(
        'Sync README files, wikis, and documentation from repositories',
      ),
      longDescription: tt(
        'Connect your GitHub repositories to train your AI agent on your technical documentation. Open syncs README files, wikis, and markdown documentation to give your AI agent deep understanding of your codebase and processes.',
      ),
      logoUrl: 'https://cdn.simpleicons.org/github/181717',
      icon: '🐙',
      color: '#24292e',
      features: [
        {
          title: tt('Repository Docs'),
          description: tt(
            'Sync README files and documentation from selected repositories.',
          ),
        },
        {
          title: tt('Wiki Integration'),
          description: tt(
            'Import GitHub Wiki pages for comprehensive documentation coverage.',
          ),
        },
        {
          title: tt('Markdown Support'),
          description: tt(
            'Full markdown parsing with code block and formatting preservation.',
          ),
        },
        {
          title: tt('Branch Selection'),
          description: tt('Choose which branches to sync documentation from.'),
        },
      ],
      syncCapabilities: [
        {
          name: tt('Repositories'),
          description: tt(
            'Repository metadata including descriptions, topics, and basic information.',
          ),
        },
        {
          name: tt('Commits'),
          description: tt(
            'Commit history with messages, authors, and timestamps from specified branches.',
          ),
        },
        {
          name: tt('Issues'),
          description: tt(
            'GitHub issues with titles, bodies, labels, assignees, and comments.',
          ),
        },
        {
          name: tt('Pull Requests'),
          description: tt(
            'Pull requests with descriptions, review status, commits, and review comments.',
          ),
        },
        {
          name: tt('Comments'),
          description: tt(
            'Issue comments and commit comments for discussion context.',
          ),
        },
        {
          name: tt('Branches'),
          description: tt(
            'Branch information and protection rules from repositories.',
          ),
        },
      ],
      useCases: [
        tt('Train AI on API documentation'),
        tt('Sync developer guides and tutorials'),
        tt('Import contribution guidelines'),
        tt('Connect architecture decision records'),
        tt('Sync deployment and ops documentation'),
        tt('Import coding standards and best practices'),
      ],
      setupSteps: [
        tt(
          'Go to GitHub Settings → Developer settings → Personal access tokens',
        ),
        tt(
          'Generate a new token with repo scope (and org:read for organization repos)',
        ),
        tt('Copy the token for use in Open'),
        tt('In Open, go to AI Training → Sources → Connect GitHub'),
        tt(
          'Enter your token and specify repositories (e.g., "org/repo" or "org/*" for all)',
        ),
        tt(
          'Optionally specify branches to sync (default branch is used if not specified)',
        ),
      ],
      authMethods: ['Personal Access Token', 'OAuth'],
      syncModes: {
        fullRefresh: true,
        incremental: true,
      },
      requirements: [
        tt('Personal Access Token or OAuth credentials'),
        tt('List of repositories to sync (format: org/repo)'),
        tt('Appropriate permissions for private repositories'),
      ],
      notes: [
        tt('Use multiple tokens separated by "," to load balance API quota'),
        tt('Most streams support incremental sync (append + deduplication)'),
        tt(
          'Specify branches like "org/repo/branch1,branch2" to sync specific branches',
        ),
        tt(
          'Invalid or inaccessible repository names are skipped with a warning',
        ),
        tt('Syncs commits, issues, PRs, reviews, comments, and more'),
      ],
    },
    // Slack
    {
      slug: 'slack',
      name: 'Slack',
      category: 'training' as const,
      description: tt('Learn from channel history, threads, and conversations'),
      longDescription: tt(
        "Connect your Slack workspace to train your AI agent on your team's conversations and knowledge sharing. Open syncs channel messages, threads, and shared files to capture institutional knowledge.",
      ),
      icon: '💬',
      color: '#4A154B',
      features: [
        {
          title: tt('Channel Sync'),
          description: tt(
            'Sync messages from selected public and private channels.',
          ),
        },
        {
          title: tt('Thread Context'),
          description: tt(
            'Capture full thread conversations for complete context.',
          ),
        },
        {
          title: tt('File Extraction'),
          description: tt('Extract content from shared documents and files.'),
        },
        {
          title: tt('User Context'),
          description: tt(
            'Understand who shared information for authority weighting.',
          ),
        },
      ],
      syncCapabilities: [
        {
          name: tt('Channels'),
          description: tt(
            'Channel metadata including public and private channels the bot is a member of (via Conversations API).',
          ),
        },
        {
          name: tt('Channel Messages'),
          description: tt(
            'Messages from non-archived public and private channels. The Slack App must be added to channels to sync messages.',
          ),
        },
        {
          name: tt('Threads'),
          description: tt(
            'Full thread conversations and replies with configurable lookback window.',
          ),
        },
        {
          name: tt('Users'),
          description: tt(
            'Workspace user information including profiles and basic metadata.',
          ),
        },
      ],
      useCases: [
        tt('Capture tribal knowledge from team discussions'),
        tt('Train AI on support channel Q&As'),
        tt('Learn from engineering discussions'),
        tt('Import sales team win/loss insights'),
        tt('Sync customer feedback discussions'),
        tt('Capture decision-making context'),
      ],
      setupSteps: [
        tt(
          'Create a Slack app at api.slack.com/apps and select your workspace',
        ),
        tt(
          'Go to OAuth & Permissions and add Bot Token Scopes: channels:history, channels:join, channels:read, users:read',
        ),
        tt(
          'Install the app to your workspace and copy the Bot User OAuth Token',
        ),
        tt(
          'Add the app to channels you want to sync (click channel → Integrations → Add apps)',
        ),
        tt('In Open, go to AI Training → Sources → Connect Slack'),
        tt(
          'Authenticate via OAuth or paste your Bot Token, then select channels',
        ),
      ],
      authMethods: ['OAuth (recommended)', 'Bot Token'],
      syncModes: {
        fullRefresh: true,
        incremental: true,
      },
      requirements: [
        tt('Slack app with appropriate Bot Token Scopes'),
        tt('App must be added to channels to sync messages'),
        tt('Required scopes: channels:history, channels:read, users:read'),
      ],
      notes: [
        tt('The app only syncs from channels it has been explicitly added to'),
        tt('Enable "join_channels" to auto-join all public channels'),
        tt(
          'For private channels, manually add the bot even with join_channels enabled',
        ),
        tt('Configure "Threads Lookback window" for historical thread data'),
        tt('Monitor for rate limits when syncing large historical data sets'),
        tt('Increase workers count for faster sync (may trigger rate limits)'),
      ],
    },
    // Continue with remaining integrations...
    // Google Sheets
    {
      slug: 'google-sheets',
      name: 'Google Sheets',
      category: 'training' as const,
      description: tt('Import structured data from spreadsheets'),
      longDescription: tt(
        'Connect your Google Sheets to train your AI agent on structured data. Open syncs all sheets within a spreadsheet, extracting rows as knowledge. Perfect for FAQs, product information, pricing tables, and any structured content.',
      ),
      icon: '📊',
      color: '#0F9D58',
      features: [
        {
          title: tt('Sheet Sync'),
          description: tt(
            'Sync all sheets within a spreadsheet, with each sheet becoming a separate data stream.',
          ),
        },
        {
          title: tt('Header Detection'),
          description: tt(
            'Automatically detects column headers and uses them as field names for structured extraction.',
          ),
        },
        {
          title: tt('Row-based Learning'),
          description: tt(
            'Each row becomes a knowledge entry, perfect for FAQ lists and product catalogs.',
          ),
        },
        {
          title: tt('Named Ranges'),
          description: tt(
            'Optionally sync specific named ranges instead of entire sheets.',
          ),
        },
      ],
      syncCapabilities: [
        {
          name: tt('Sheets'),
          description: tt(
            'Each sheet tab within the spreadsheet is synced as a separate stream with all rows and columns.',
          ),
        },
        {
          name: tt('Cell Values'),
          description: tt(
            'All cell values including text, numbers, dates, and formulas (evaluated results).',
          ),
        },
      ],
      useCases: [
        tt('FAQ sheets with questions and answers'),
        tt('Product catalogs with specifications'),
        tt('Pricing tables and comparison charts'),
        tt('Team directories and contact lists'),
        tt('Process checklists and procedures'),
        tt('Training content in tabular format'),
      ],
      setupSteps: [
        tt('In Open, go to AI Training → Sources → Connect Google Sheets'),
        tt('Click "Authenticate via Google (OAuth)" and authorize access'),
        tt('Copy the spreadsheet link from your browser address bar'),
        tt('Paste the link and configure which sheets to sync'),
        tt('Optionally rename sheets or specify named ranges'),
        tt('Start syncing your spreadsheet data'),
      ],
      authMethods: ['OAuth (recommended)', 'Service Account Key'],
      syncModes: {
        fullRefresh: true,
        incremental: false,
      },
      requirements: [
        tt('Google Sheets spreadsheet link'),
        tt('Read access to the spreadsheet'),
        tt(
          'For Service Account: JSON key and sheet shared with service account email',
        ),
      ],
      notes: [
        tt(
          'Syncs from a single spreadsheet - use multiple connections for multiple spreadsheets',
        ),
        tt('First row is typically used as column headers'),
        tt('Empty rows and columns are skipped'),
        tt(
          'Formulas are synced as their evaluated values, not the formula itself',
        ),
        tt('For multiple spreadsheets, use Google Drive connector instead'),
      ],
    },
    // Airtable
    {
      slug: 'airtable',
      name: 'Airtable',
      category: 'training' as const,
      description: tt('Connect bases and tables from Airtable'),
      longDescription: tt(
        'Connect your Airtable workspace to train your AI agent on structured data. Open syncs all tables from your bases, including all field types, attachments, and linked records for comprehensive knowledge.',
      ),
      icon: '📋',
      logoUrl: 'https://cdn.simpleicons.org/airtable/18BFFF',
      color: '#18BFFF',
      features: [
        {
          title: tt('Full Base Sync'),
          description: tt(
            'Sync all tables from selected bases with complete field data and relationships.',
          ),
        },
        {
          title: tt('Rich Field Support'),
          description: tt(
            'Handles all Airtable field types including attachments, lookups, rollups, and formulas.',
          ),
        },
        {
          title: tt('Linked Records'),
          description: tt(
            'Preserves relationships between tables through linked record fields.',
          ),
        },
        {
          title: tt('View Filtering'),
          description: tt(
            'Optionally sync only records visible in specific views.',
          ),
        },
      ],
      syncCapabilities: [
        {
          name: tt('Tables'),
          description: tt(
            'All tables from selected bases with complete record data and metadata.',
          ),
        },
        {
          name: tt('Records'),
          description: tt(
            'Individual records with all field values, including complex field types like attachments and lookups.',
          ),
        },
        {
          name: tt('Attachments'),
          description: tt(
            'File attachments with URLs and metadata (actual file content requires additional processing).',
          ),
        },
      ],
      useCases: [
        tt('Product databases with rich metadata'),
        tt('Content calendars and editorial data'),
        tt('CRM-style contact databases'),
        tt('Project tracking and documentation'),
        tt('Inventory and catalog management'),
        tt('Team wikis in tabular format'),
      ],
      setupSteps: [
        tt(
          'Go to airtable.com/create/tokens to create a Personal Access Token',
        ),
        tt('Add scope: schema.bases:read and data.records:read'),
        tt('Select which bases to grant access to'),
        tt('In Open, go to AI Training → Sources → Connect Airtable'),
        tt('Choose OAuth or paste your Personal Access Token'),
        tt('Select bases and tables to sync'),
      ],
      authMethods: ['OAuth (recommended)', 'Personal Access Token'],
      syncModes: {
        fullRefresh: true,
        incremental: false,
      },
      requirements: [
        tt(
          'Personal Access Token with schema.bases:read and data.records:read scopes',
        ),
        tt('Access to the bases you want to sync'),
      ],
      notes: [
        tt('OAuth is recommended for simpler setup'),
        tt(
          'If you rename a table in Airtable, reset the connection to continue syncing',
        ),
        tt('Deleted tables stop syncing automatically'),
        tt('Formula fields sync their computed values'),
        tt('Attachment URLs are synced but files need separate download'),
      ],
    },
    // MongoDB
    {
      slug: 'mongodb',
      name: 'MongoDB',
      category: 'training' as const,
      description: tt('Import document collections from MongoDB'),
      longDescription: tt(
        'Connect your MongoDB database to train your AI agent on document collections. Open syncs collections using Change Data Capture (CDC) for real-time updates or full refresh for complete snapshots.',
      ),
      icon: '🍃',
      logoUrl: 'https://cdn.simpleicons.org/mongodb/47A248',
      color: '#00684A',
      features: [
        {
          title: tt('CDC Replication'),
          description: tt(
            'Change Data Capture for real-time incremental sync of inserts, updates, and deletes.',
          ),
        },
        {
          title: tt('Schema-less Mode'),
          description: tt(
            'Read documents without assuming a fixed schema - handles varying document structures.',
          ),
        },
        {
          title: tt('Collection Sync'),
          description: tt(
            'Sync entire collections or filter by specific criteria.',
          ),
        },
        {
          title: tt('Cluster Support'),
          description: tt(
            'Works with MongoDB Atlas and self-hosted replica set clusters.',
          ),
        },
      ],
      syncCapabilities: [
        {
          name: tt('Collections'),
          description: tt(
            'Full collection data with all documents and their nested structures.',
          ),
        },
        {
          name: tt('Documents'),
          description: tt(
            'Individual documents with all fields, including nested objects and arrays.',
          ),
        },
        {
          name: tt('Change Streams'),
          description: tt(
            'Real-time capture of inserts, updates, and deletes via CDC (requires replica set).',
          ),
        },
      ],
      useCases: [
        tt('Product catalogs stored in MongoDB'),
        tt('User profiles and preferences'),
        tt('Content management system data'),
        tt('Application configuration data'),
        tt('Logging and analytics data'),
        tt('IoT sensor data and metrics'),
      ],
      setupSteps: [
        tt(
          'Create a database user with read access (and oplog access for CDC)',
        ),
        tt('Get your MongoDB connection string from Atlas or your cluster'),
        tt('Ensure your cluster is a replica set (required for CDC)'),
        tt('In Open, go to AI Training → Sources → Connect MongoDB'),
        tt('Enter your connection string and credentials'),
        tt('Select collections to sync and choose sync mode'),
      ],
      authMethods: ['Username/Password via Connection String'],
      syncModes: {
        fullRefresh: true,
        incremental: true,
      },
      requirements: [
        tt('MongoDB replica set cluster (Atlas or self-hosted)'),
        tt('Database user with read permissions'),
        tt('For CDC: oplog access for the user'),
        tt('Network access to MongoDB (whitelist IPs if needed)'),
      ],
      notes: [
        tt(
          'CDC requires a replica set - standalone MongoDB instances only support full refresh',
        ),
        tt('Schema-less mode handles documents with varying structures'),
        tt('For large collections, initial sync may take significant time'),
        tt('MongoDB 3.6+ required for Change Streams'),
        tt('Atlas M10+ clusters recommended for production CDC'),
      ],
    },
    // SharePoint
    {
      slug: 'sharepoint',
      name: 'SharePoint',
      category: 'training' as const,
      description: tt('Sync enterprise document libraries from SharePoint'),
      longDescription: tt(
        'Connect your Microsoft SharePoint to train your AI agent on enterprise documents. Open syncs files from document libraries, extracting content from Office documents, PDFs, and other supported formats.',
      ),
      icon: '📁',
      color: '#038387',
      features: [
        {
          title: tt('Document Libraries'),
          description: tt(
            'Sync entire document libraries with folder structure preservation.',
          ),
        },
        {
          title: tt('Office Document Support'),
          description: tt(
            'Extract text content from Word, Excel, PowerPoint, and PDF files.',
          ),
        },
        {
          title: tt('Site Selection'),
          description: tt(
            'Choose specific SharePoint sites and libraries to sync.',
          ),
        },
        {
          title: tt('Metadata Sync'),
          description: tt(
            'Include document metadata like author, modified date, and custom columns.',
          ),
        },
      ],
      syncCapabilities: [
        {
          name: tt('Files'),
          description: tt(
            'Document files with content extraction for supported formats (Office docs, PDFs).',
          ),
        },
        {
          name: tt('Folders'),
          description: tt(
            'Folder structure and organization from document libraries.',
          ),
        },
        {
          name: tt('Metadata'),
          description: tt(
            'Document properties, custom columns, and SharePoint metadata.',
          ),
        },
      ],
      useCases: [
        tt('Corporate policy documents'),
        tt('HR handbooks and procedures'),
        tt('Sales collateral and presentations'),
        tt('Technical documentation'),
        tt('Compliance and regulatory docs'),
        tt('Training materials and guides'),
      ],
      setupSteps: [
        tt(
          'Register an app in Azure Active Directory with SharePoint permissions',
        ),
        tt('Grant Sites.Read.All or more specific permissions'),
        tt('Collect your Azure AD credentials (Client ID, Secret, Tenant ID)'),
        tt('In Open, go to AI Training → Sources → Connect SharePoint'),
        tt('Enter your Azure AD credentials and SharePoint site URL'),
        tt('Select document libraries to sync'),
      ],
      authMethods: ['Azure AD App Registration (OAuth)'],
      syncModes: {
        fullRefresh: true,
        incremental: true,
      },
      requirements: [
        tt('Azure Active Directory app with SharePoint API permissions'),
        tt('Client ID, Client Secret, and Tenant ID'),
        tt('SharePoint site URL'),
        tt('Appropriate permissions for the sites/libraries you want to sync'),
      ],
      notes: [
        tt('Requires Azure AD app registration with appropriate permissions'),
        tt('Large document libraries may take time for initial sync'),
        tt(
          'Binary files are synced but need document extraction for text content',
        ),
        tt('Supports SharePoint Online (Microsoft 365) - on-premises may vary'),
      ],
    },
    // Asana
    {
      slug: 'asana',
      name: 'Asana',
      category: 'training' as const,
      description: tt('Import project documentation and tasks from Asana'),
      longDescription: tt(
        'Connect your Asana workspace to train your AI agent on project documentation. Open syncs tasks, projects, and their descriptions to capture institutional knowledge about processes and workflows.',
      ),
      icon: '📅',
      color: '#F06A6A',
      features: [
        {
          title: tt('Project Sync'),
          description: tt(
            'Sync all projects with their descriptions, sections, and organizational structure.',
          ),
        },
        {
          title: tt('Task Content'),
          description: tt(
            'Extract task names, descriptions, comments, and attachments.',
          ),
        },
        {
          title: tt('Custom Fields'),
          description: tt(
            'Include custom field values for rich metadata on tasks.',
          ),
        },
        {
          title: tt('Workspace Support'),
          description: tt(
            'Sync from specific workspaces or across your entire organization.',
          ),
        },
      ],
      syncCapabilities: [
        {
          name: tt('Projects'),
          description: tt(
            'All projects with names, descriptions, and section structure.',
          ),
        },
        {
          name: tt('Tasks'),
          description: tt(
            'Tasks with names, descriptions, assignees, due dates, and custom fields.',
          ),
        },
        {
          name: tt('Stories'),
          description: tt('Task comments and activity history for context.'),
        },
        {
          name: tt('Users'),
          description: tt('Workspace members with names and email addresses.'),
        },
      ],
      useCases: [
        tt('Process documentation in project descriptions'),
        tt('Recurring task templates and checklists'),
        tt('Team workflows and procedures'),
        tt('Meeting notes and action items'),
        tt('Project status updates and decisions'),
        tt('Onboarding task lists'),
      ],
      setupSteps: [
        tt('Go to Asana Developer Console and create a Personal Access Token'),
        tt('Or set up OAuth for automatic token management'),
        tt('In Open, go to AI Training → Sources → Connect Asana'),
        tt('Authenticate with your access token or OAuth'),
        tt('Select workspaces and projects to sync'),
        tt('Configure which data types to include'),
      ],
      authMethods: ['OAuth', 'Personal Access Token'],
      syncModes: {
        fullRefresh: true,
        incremental: true,
      },
      requirements: [
        tt('Asana account with access to workspaces you want to sync'),
        tt('Personal Access Token or OAuth app credentials'),
      ],
      notes: [
        tt(
          'Task attachments sync as URLs - file content needs separate extraction',
        ),
        tt('Completed tasks are included by default'),
        tt('Subtasks are synced as separate task entries'),
        tt('Rate limits apply - large workspaces may take time to sync'),
      ],
    },
    // Monday.com
    {
      slug: 'monday',
      name: 'Monday.com',
      category: 'training' as const,
      description: tt('Sync board data and documentation from Monday.com'),
      longDescription: tt(
        'Connect your Monday.com workspace to train your AI agent on board data. Open syncs boards, items, and their values to capture project information, processes, and structured content.',
      ),
      icon: '📊',
      color: '#FF3D57',
      features: [
        {
          title: tt('Board Sync'),
          description: tt(
            'Sync all boards with groups, items, and column configurations.',
          ),
        },
        {
          title: tt('Column Values'),
          description: tt(
            'Extract all column types including text, numbers, dates, and dropdowns.',
          ),
        },
        {
          title: tt('Updates & Comments'),
          description: tt(
            'Include item updates and comments for discussion context.',
          ),
        },
        {
          title: tt('Workspace Filtering'),
          description: tt('Select specific workspaces or boards to sync.'),
        },
      ],
      syncCapabilities: [
        {
          name: tt('Boards'),
          description: tt(
            'Board metadata with groups, columns, and configuration.',
          ),
        },
        {
          name: tt('Items'),
          description: tt(
            'All items with their column values across different column types.',
          ),
        },
        {
          name: tt('Updates'),
          description: tt(
            'Item updates and replies for communication history.',
          ),
        },
        {
          name: tt('Teams'),
          description: tt('Team and user information from the workspace.'),
        },
      ],
      useCases: [
        tt('Project tracking boards'),
        tt('Process documentation in item descriptions'),
        tt('CRM-style contact boards'),
        tt('Content calendars and editorial data'),
        tt('Sprint and development tracking'),
        tt('Client management boards'),
      ],
      setupSteps: [
        tt('Go to your Monday.com profile → Admin → API'),
        tt('Generate a new API Token'),
        tt('In Open, go to AI Training → Sources → Connect Monday.com'),
        tt('Paste your API Token'),
        tt('Select boards to sync'),
        tt('Configure sync frequency'),
      ],
      authMethods: ['API Token'],
      syncModes: {
        fullRefresh: true,
        incremental: true,
      },
      requirements: [
        tt('Monday.com API Token with read access'),
        tt('Access to the boards you want to sync'),
      ],
      notes: [
        tt('API token scopes determine which boards are accessible'),
        tt('Complex column types like formulas sync as their display values'),
        tt('Subitems are synced as separate entries'),
        tt('Rate limits may apply for large workspaces'),
      ],
    },
    // Snowflake
    {
      slug: 'snowflake',
      name: 'Snowflake',
      category: 'training' as const,
      description: tt('Connect your Snowflake data warehouse'),
      longDescription: tt(
        'Connect your Snowflake data warehouse to train your AI agent on structured business data. Open syncs tables and views, making your data warehouse knowledge accessible to your AI agent.',
      ),
      icon: '❄️',
      logoUrl: 'https://cdn.simpleicons.org/snowflake/29B5E8',
      color: '#29B5E8',
      features: [
        {
          title: tt('Table Sync'),
          description: tt(
            'Sync tables and views from your Snowflake database.',
          ),
        },
        {
          title: tt('Schema Discovery'),
          description: tt('Automatic schema detection for all data types.'),
        },
        {
          title: tt('Query Filtering'),
          description: tt('Optionally filter data using SQL WHERE clauses.'),
        },
        {
          title: tt('Role-based Access'),
          description: tt('Respect Snowflake roles and access controls.'),
        },
      ],
      syncCapabilities: [
        {
          name: tt('Tables'),
          description: tt('Full table data with all columns and rows.'),
        },
        {
          name: tt('Views'),
          description: tt(
            'Materialized and regular views with their computed data.',
          ),
        },
        {
          name: tt('Schemas'),
          description: tt('Schema metadata for structure understanding.'),
        },
      ],
      useCases: [
        tt('Business intelligence data'),
        tt('Aggregated metrics and KPIs'),
        tt('Customer analytics data'),
        tt('Product usage statistics'),
        tt('Financial reporting data'),
        tt('Marketing campaign data'),
      ],
      setupSteps: [
        tt('Create a Snowflake user with read access to required schemas'),
        tt('Note your account identifier, warehouse, database, and schema'),
        tt('In Open, go to AI Training → Sources → Connect Snowflake'),
        tt('Enter your Snowflake credentials and connection details'),
        tt('Select tables and views to sync'),
        tt('Configure sync frequency'),
      ],
      authMethods: ['Username/Password', 'Key Pair Authentication'],
      syncModes: {
        fullRefresh: true,
        incremental: true,
      },
      requirements: [
        tt('Snowflake account identifier'),
        tt('User credentials with SELECT permissions'),
        tt('Warehouse, database, and schema names'),
        tt('Network access (may need to whitelist IPs)'),
      ],
      notes: [
        tt('Large tables may take significant time for initial sync'),
        tt('Consider using views to filter/aggregate data before syncing'),
        tt('Warehouse must be running for sync to work'),
        tt('Incremental sync uses standard CDC patterns'),
      ],
    },
    // BigQuery
    {
      slug: 'bigquery',
      name: 'BigQuery',
      category: 'training' as const,
      description: tt('Sync your Google BigQuery data warehouse'),
      longDescription: tt(
        'Connect your Google BigQuery data warehouse to train your AI agent on analytics data. Open syncs tables and views, bringing your structured business intelligence data to your AI agent.',
      ),
      icon: '🔷',
      logoUrl: 'https://cdn.simpleicons.org/googlebigquery/669DF6',
      color: '#4285F4',
      features: [
        {
          title: tt('Dataset Sync'),
          description: tt('Sync tables from selected BigQuery datasets.'),
        },
        {
          title: tt('Partitioned Tables'),
          description: tt(
            'Support for partitioned tables with efficient incremental sync.',
          ),
        },
        {
          title: tt('View Support'),
          description: tt('Sync both tables and materialized views.'),
        },
        {
          title: tt('GCP Integration'),
          description: tt(
            'Native Google Cloud authentication with service accounts.',
          ),
        },
      ],
      syncCapabilities: [
        {
          name: tt('Tables'),
          description: tt(
            'Full table data including all columns and nested/repeated fields.',
          ),
        },
        {
          name: tt('Views'),
          description: tt('Materialized views with their computed data.'),
        },
        {
          name: tt('Nested Data'),
          description: tt('Support for STRUCT and ARRAY column types.'),
        },
      ],
      useCases: [
        tt('Analytics dashboards data'),
        tt('Customer segmentation data'),
        tt('Product metrics and usage stats'),
        tt('Marketing attribution data'),
        tt('Financial aggregations'),
        tt('Data science model outputs'),
      ],
      setupSteps: [
        tt('Create a GCP service account with BigQuery Data Viewer role'),
        tt('Generate and download a JSON key for the service account'),
        tt('In Open, go to AI Training → Sources → Connect BigQuery'),
        tt('Upload your service account JSON key'),
        tt('Enter your GCP project ID and dataset names'),
        tt('Select tables to sync'),
      ],
      authMethods: ['Service Account (JSON Key)'],
      syncModes: {
        fullRefresh: true,
        incremental: true,
      },
      requirements: [
        tt('GCP service account with BigQuery Data Viewer role'),
        tt('Service account JSON key file'),
        tt('Project ID and dataset names'),
      ],
      notes: [
        tt('Costs may apply for BigQuery data scanned during sync'),
        tt('Large tables benefit from partitioning for incremental sync'),
        tt('Nested and repeated fields are flattened in the output'),
        tt('Consider using views to pre-aggregate data'),
      ],
    },
    // Intercom Articles
    {
      slug: 'intercom-articles',
      name: 'Intercom Articles',
      category: 'training' as const,
      description: tt('Import help center articles from Intercom'),
      longDescription: tt(
        'Connect your Intercom Help Center to train your AI agent on your support articles. Open syncs all published articles with their content, categories, and metadata.',
      ),
      icon: '💬',
      color: '#1F8DED',
      features: [
        {
          title: tt('Article Sync'),
          description: tt(
            'Sync all published help center articles with full content.',
          ),
        },
        {
          title: tt('Collection Structure'),
          description: tt(
            'Maintain article organization through collections and sections.',
          ),
        },
        {
          title: tt('Multilingual Support'),
          description: tt('Sync articles across all configured languages.'),
        },
        {
          title: tt('Author Tracking'),
          description: tt(
            'Include article authors and last update timestamps.',
          ),
        },
      ],
      syncCapabilities: [
        {
          name: tt('Articles'),
          description: tt(
            'Full article content including title, body, and metadata.',
          ),
        },
        {
          name: tt('Collections'),
          description: tt(
            'Help center collections and their organization structure.',
          ),
        },
        {
          name: tt('Sections'),
          description: tt('Sections within collections for content hierarchy.'),
        },
      ],
      useCases: [
        tt('Train AI on existing help content'),
        tt('Ensure AI answers match your help center'),
        tt('Sync product documentation'),
        tt('Import onboarding guides'),
        tt('Keep AI updated with new articles'),
      ],
      setupSteps: [
        tt('Go to Intercom Settings → Integrations → Developer Hub'),
        tt('Create a new app or use an existing one'),
        tt('Generate an Access Token with read permissions'),
        tt('In Open, go to AI Training → Sources → Connect Intercom'),
        tt('Enter your Access Token'),
        tt('Articles will sync automatically'),
      ],
      authMethods: ['Access Token'],
      syncModes: {
        fullRefresh: true,
        incremental: true,
      },
      requirements: [
        tt('Intercom Access Token with Articles read permission'),
        tt('Published articles in your Help Center'),
      ],
      notes: [
        tt('Only published articles are synced by default'),
        tt('Draft articles can be optionally included'),
        tt('Article body is synced as HTML - formatting preserved'),
        tt('Incremental sync picks up new and updated articles'),
      ],
    },
    // Freshdesk KB
    {
      slug: 'freshdesk-kb',
      name: 'Freshdesk KB',
      category: 'training' as const,
      description: tt('Sync solution articles from Freshdesk'),
      longDescription: tt(
        'Connect your Freshdesk Knowledge Base to train your AI agent on your support articles. Open syncs all solution articles, categories, and folders to give your AI agent comprehensive knowledge of your help content.',
      ),
      icon: '📗',
      logoUrl: '/helpdicks/freshdesk.png',
      color: '#25C16F',
      features: [
        {
          title: tt('Solution Article Sync'),
          description: tt(
            'Sync all solution articles with full content and formatting.',
          ),
        },
        {
          title: tt('Category Structure'),
          description: tt(
            'Maintain folder and category organization for context.',
          ),
        },
        {
          title: tt('Draft Support'),
          description: tt(
            'Optionally include draft articles for comprehensive coverage.',
          ),
        },
        {
          title: tt('Multilingual'),
          description: tt('Sync articles across multiple language portals.'),
        },
      ],
      syncCapabilities: [
        {
          name: tt('Solution Articles'),
          description: tt(
            'Full article content including title, description, and body.',
          ),
        },
        {
          name: tt('Categories'),
          description: tt('Solution categories for organizational context.'),
        },
        {
          name: tt('Folders'),
          description: tt('Folder structure within categories.'),
        },
      ],
      useCases: [
        tt('Train AI on existing knowledge base'),
        tt('Sync product documentation'),
        tt('Import troubleshooting guides'),
        tt('Keep AI updated with new articles'),
        tt('Ensure consistent support answers'),
      ],
      setupSteps: [
        tt('Go to Freshdesk Admin → Profile Settings → API Key'),
        tt('Copy your API Key'),
        tt('In Open, go to AI Training → Sources → Connect Freshdesk KB'),
        tt('Enter your Freshdesk domain and API Key'),
        tt('Select categories to sync'),
        tt('Articles will sync automatically'),
      ],
      authMethods: ['API Key'],
      syncModes: {
        fullRefresh: true,
        incremental: true,
      },
      requirements: [
        tt('Freshdesk API Key'),
        tt('Freshdesk domain (yourcompany.freshdesk.com)'),
        tt('Published solution articles'),
      ],
      notes: [
        tt('API Key found in Profile Settings, not Admin settings'),
        tt('Only published articles sync by default'),
        tt('Article attachments are referenced but not downloaded'),
        tt('Folder permissions respected during sync'),
      ],
    },
    // HubSpot KB
    {
      slug: 'hubspot-kb',
      name: 'HubSpot KB',
      category: 'training' as const,
      description: tt('Import knowledge base articles from HubSpot'),
      longDescription: tt(
        'Connect your HubSpot Knowledge Base to train your AI agent on your support articles. Open syncs all published articles with their content, categories, and metadata for comprehensive AI training.',
      ),
      icon: '🟠',
      logoUrl: 'https://cdn.simpleicons.org/hubspot/FF7A59',
      color: '#FF7A59',
      features: [
        {
          title: tt('Article Sync'),
          description: tt(
            'Sync all published knowledge base articles with full content.',
          ),
        },
        {
          title: tt('Category Organization'),
          description: tt('Maintain article categories and subcategories.'),
        },
        {
          title: tt('Rich Content'),
          description: tt(
            'Preserve formatting, images, and embedded content references.',
          ),
        },
        {
          title: tt('Automatic Updates'),
          description: tt('Changes in HubSpot sync automatically to your AI.'),
        },
      ],
      syncCapabilities: [
        {
          name: tt('Articles'),
          description: tt(
            'Full article content including title, body, and metadata.',
          ),
        },
        {
          name: tt('Categories'),
          description: tt('Knowledge base categories and organization.'),
        },
        {
          name: tt('Tags'),
          description: tt('Article tags for topic classification.'),
        },
      ],
      useCases: [
        tt('Train AI on existing help content'),
        tt('Sync product documentation'),
        tt('Import FAQ content'),
        tt('Keep AI updated with new articles'),
        tt('Consistent support across channels'),
      ],
      setupSteps: [
        tt('Go to HubSpot Settings → Integrations → Private Apps'),
        tt('Create a new Private App with CMS read scope'),
        tt('Copy the Access Token'),
        tt('In Open, go to AI Training → Sources → Connect HubSpot KB'),
        tt('Enter your Access Token'),
        tt('Select which knowledge bases to sync'),
      ],
      authMethods: ['Private App Token', 'OAuth'],
      syncModes: {
        fullRefresh: true,
        incremental: true,
      },
      requirements: [
        tt('HubSpot account with Knowledge Base feature'),
        tt('Private App with CMS read permissions'),
        tt('Published knowledge base articles'),
      ],
      notes: [
        tt('Requires HubSpot Service Hub Professional or Enterprise'),
        tt('Only published articles are synced'),
        tt('Article analytics not included in sync'),
        tt('Multi-language articles sync as separate entries'),
      ],
    },
    // Coda
    {
      slug: 'coda',
      name: 'Coda',
      category: 'training' as const,
      description: tt('Sync docs and structured content from Coda'),
      longDescription: tt(
        'Connect your Coda workspace to train your AI agent on your docs. Open syncs pages, tables, and formulas from your Coda docs, capturing both narrative content and structured data.',
      ),
      icon: '📝',
      color: '#F46A54',
      features: [
        {
          title: tt('Doc Sync'),
          description: tt('Sync entire Coda docs with all pages and subpages.'),
        },
        {
          title: tt('Table Data'),
          description: tt(
            'Extract structured data from Coda tables with all column types.',
          ),
        },
        {
          title: tt('Formula Values'),
          description: tt('Sync computed formula values for derived content.'),
        },
        {
          title: tt('Page Content'),
          description: tt(
            'Full page content including text, embeds, and layouts.',
          ),
        },
      ],
      syncCapabilities: [
        {
          name: tt('Docs'),
          description: tt('Document metadata and structure.'),
        },
        {
          name: tt('Pages'),
          description: tt('All pages with content, including nested pages.'),
        },
        {
          name: tt('Tables'),
          description: tt('Table data with rows and all column values.'),
        },
      ],
      useCases: [
        tt('Team wikis and documentation'),
        tt('Project tracking docs'),
        tt('Process playbooks'),
        tt('Meeting notes and decisions'),
        tt('Product specs and PRDs'),
        tt('Team databases and directories'),
      ],
      setupSteps: [
        tt('Go to Coda Account Settings → API Settings'),
        tt('Generate a new API Token'),
        tt('In Open, go to AI Training → Sources → Connect Coda'),
        tt('Enter your API Token'),
        tt('Select docs to sync'),
        tt('Configure sync frequency'),
      ],
      authMethods: ['API Token'],
      syncModes: {
        fullRefresh: true,
        incremental: false,
      },
      requirements: [
        tt('Coda API Token'),
        tt('Access to the docs you want to sync'),
      ],
      notes: [
        tt('API token determines which docs are accessible'),
        tt('Table formulas sync as their computed values'),
        tt('Embedded content may not fully transfer'),
        tt('Large docs with many tables may take time to sync'),
      ],
    },
    // Dropbox
    {
      slug: 'dropbox',
      name: 'Dropbox',
      category: 'training' as const,
      description: tt('Import documents and files from Dropbox'),
      longDescription: tt(
        'Connect your Dropbox to train your AI agent on your documents. Open syncs files from selected folders, extracting content from documents, PDFs, and other text-based files.',
      ),
      icon: '📦',
      logoUrl: 'https://cdn.simpleicons.org/dropbox/0061FF',
      color: '#0061FF',
      features: [
        {
          title: tt('Folder Sync'),
          description: tt(
            'Sync files from selected folders including subfolders.',
          ),
        },
        {
          title: tt('Document Extraction'),
          description: tt(
            'Extract text content from PDFs, Office docs, and text files.',
          ),
        },
        {
          title: tt('Shared Folders'),
          description: tt('Access shared folders and team content.'),
        },
        {
          title: tt('File Filtering'),
          description: tt('Filter by file types using glob patterns.'),
        },
      ],
      syncCapabilities: [
        {
          name: tt('Text Documents'),
          description: tt(
            'PDF, DOCX, TXT and other text-based files. Binary files (images, videos, executables) are excluded.',
          ),
        },
        {
          name: tt('Metadata'),
          description: tt('File names, paths, sizes, and modification dates.'),
        },
        {
          name: tt('Folders'),
          description: tt('Folder structure for organization context.'),
        },
      ],
      useCases: [
        tt('Shared team documentation'),
        tt('Client files and deliverables'),
        tt('Company policies and procedures'),
        tt('Training materials'),
        tt('Archived content and records'),
      ],
      setupSteps: [
        tt('In Open, go to AI Training → Sources → Connect Dropbox'),
        tt('Click "Authenticate with Dropbox" and authorize access'),
        tt('Select the folder path to sync from'),
        tt('Configure file type filters if needed'),
        tt('Start syncing your Dropbox content'),
      ],
      authMethods: ['OAuth'],
      syncModes: {
        fullRefresh: true,
        incremental: true,
      },
      requirements: [
        tt('Dropbox account with access to folders you want to sync'),
        tt('OAuth authorization'),
      ],
      notes: [
        tt(
          'Document text extraction works for common formats (PDF, DOCX, TXT)',
        ),
        tt('Binary files (images, videos, executables) are not synced'),
        tt('Only text-based documents are processed for AI training'),
        tt('Large folders may take time for initial sync'),
        tt('Respects Dropbox sharing permissions'),
      ],
    },
    // OneDrive
    {
      slug: 'onedrive',
      name: 'OneDrive',
      category: 'training' as const,
      description: tt('Connect Microsoft OneDrive documents'),
      longDescription: tt(
        'Connect your Microsoft OneDrive to train your AI agent on your documents. Open syncs files from your personal or business OneDrive, extracting content from Office documents and PDFs.',
      ),
      icon: '☁️',
      color: '#0078D4',
      features: [
        {
          title: tt('Folder Sync'),
          description: tt(
            'Sync files from specific OneDrive folders recursively.',
          ),
        },
        {
          title: tt('Office Integration'),
          description: tt(
            'Native support for Word, Excel, and PowerPoint content extraction.',
          ),
        },
        {
          title: tt('Business & Personal'),
          description: tt(
            'Works with OneDrive for Business and personal accounts.',
          ),
        },
        {
          title: tt('Shared Content'),
          description: tt('Access shared files and folders.'),
        },
      ],
      syncCapabilities: [
        {
          name: tt('Files'),
          description: tt(
            'Document files with content extraction for Office and PDF formats.',
          ),
        },
        {
          name: tt('Folders'),
          description: tt('Folder structure and organization.'),
        },
        {
          name: tt('Metadata'),
          description: tt('File properties, dates, and sharing information.'),
        },
      ],
      useCases: [
        tt('Corporate documents and templates'),
        tt('Team shared folders'),
        tt('Project deliverables'),
        tt('HR and policy documents'),
        tt('Training materials'),
        tt('Client files'),
      ],
      setupSteps: [
        tt('In Open, go to AI Training → Sources → Connect OneDrive'),
        tt('Click "Authenticate with Microsoft" and sign in'),
        tt('Authorize access to your OneDrive files'),
        tt('Select folders to sync'),
        tt('Configure file type filters if needed'),
      ],
      authMethods: ['Microsoft OAuth'],
      syncModes: {
        fullRefresh: true,
        incremental: true,
      },
      requirements: [
        tt('Microsoft account (personal or work)'),
        tt('Access to OneDrive folders you want to sync'),
      ],
      notes: [
        tt('OneDrive for Business may require admin consent for OAuth'),
        tt('Office documents are extracted as text with formatting'),
        tt('SharePoint document libraries need SharePoint connector'),
        tt('Large syncs may hit Microsoft Graph API rate limits'),
      ],
    },
    // Shopify
    {
      slug: 'shopify',
      name: 'Shopify',
      category: 'ecommerce' as const,
      description:
        'Read orders, customers, and products live during AI calls and chats',
      longDescription:
        'Connect Shopify so the AI can fetch order status, look up customers, check inventory, and create returns or refunds in real time. Whether the call is inbound support or outbound win-back, the AI has the order data the agent would need — and can act on it within your Shopify policies.',
      icon: '🛍️',
      logoUrl: 'https://cdn.simpleicons.org/shopify/7AB55C',
      color: '#7AB55C',
      features: [
        {
          title: 'Live order lookup',
          description:
            'Fetch order status, tracking, items, and totals in real time from Shopify by phone number, email, or order ID.',
        },
        {
          title: 'Returns and refunds in policy',
          description:
            'AI initiates returns and refunds inside your configured policy bounds — only escalates the edge cases.',
        },
        {
          title: 'Customer 360',
          description:
            'Customer record, purchase history, lifetime value, and tags — pulled before the AI says hello.',
        },
        {
          title: 'Inventory aware',
          description:
            'Read product availability live so the AI can answer "is X back in stock?" and offer alternatives.',
        },
      ],
      syncCapabilities: [
        {
          name: 'Orders',
          description: 'Order status, items, totals, fulfillment, returns.',
        },
        {
          name: 'Customers',
          description:
            'Customer records, lifetime value, tags, purchase history.',
        },
        {
          name: 'Products',
          description: 'Catalog, variants, prices, inventory levels.',
        },
        {
          name: 'Discounts',
          description:
            'Discount codes and promotions for win-back and cart-recovery flows.',
        },
      ],
      useCases: [
        'AI customer service that resolves order status calls in 30 seconds',
        'AI cart-recovery calls with the live cart contents',
        'Inbound returns/refunds handled within policy without an agent',
        'Win-back outbound based on Shopify segments',
        'Order confirmation calls and shipment notifications by AI',
        'AI sales agent that knows the catalog and inventory live',
      ],
      setupSteps: [
        'In Shopify Admin, go to Apps → Develop apps → Create an app',
        'Configure Admin API access scopes (orders, customers, products at minimum)',
        'Install the app and copy the Admin API access token',
        'In Open, go to Settings → Integrations → Shopify and paste the access token',
        'Select which agents and which calls can read/write Shopify data',
        'Test with a sample order lookup before going live',
      ],
      authMethods: ['Admin API access token', 'OAuth (multi-store)'],
      syncModes: {
        fullRefresh: true,
        incremental: true,
      },
      requirements: [
        'Shopify Admin access',
        'Admin API access scopes for orders, customers, products',
      ],
      notes: [
        'Refunds and order edits are made under the configured Shopify staff account',
        'Multi-store: connect each store individually or use a Shopify Plus organization',
        'Read-only mode supported for compliance-sensitive deployments',
      ],
    },
    // Stripe
    {
      slug: 'stripe',
      name: 'Stripe',
      category: 'ecommerce' as const,
      description:
        'Send payment links, charge cards, and reconcile invoices on the call',
      longDescription:
        'Connect Stripe and the AI can send payment links, charge a saved card, look up an invoice, or process a refund — live during the conversation. Useful for AI receptionists collecting balances, support resolving billing questions, and outbound dunning calls.',
      icon: '💳',
      logoUrl: 'https://cdn.simpleicons.org/stripe/635BFF',
      color: '#635BFF',
      features: [
        {
          title: 'Live payment links by SMS',
          description:
            'AI generates a Stripe payment link mid-call and texts it to the customer instantly.',
        },
        {
          title: 'Saved-card charges',
          description:
            'For customers with a card on file, the AI can charge with consent inside your policy bounds.',
        },
        {
          title: 'Invoice and balance lookups',
          description:
            'Real-time invoice status, balance, and payment history during the conversation.',
        },
        {
          title: 'Refunds in policy',
          description:
            'AI can issue refunds within configured limits and reasons; anything else warm-transfers.',
        },
      ],
      syncCapabilities: [
        {
          name: 'Customers',
          description: 'Customer records with payment methods and metadata.',
        },
        {
          name: 'Invoices',
          description: 'Outstanding and paid invoices with line items.',
        },
        {
          name: 'Subscriptions',
          description: 'Subscription status, plan, billing period, and usage.',
        },
        {
          name: 'Charges',
          description:
            'Charge history, refund status, and payment method details.',
        },
      ],
      useCases: [
        'AI dunning calls that send a payment link mid-conversation',
        'Inbound payment collection for service businesses',
        'Subscription saves: AI offers a discount and processes the change',
        'Invoice reminders by phone with live balance lookup',
        'Refund resolution within policy without ticket queueing',
      ],
      setupSteps: [
        'In Stripe Dashboard, create a restricted API key with the scopes you need',
        'In Open, go to Settings → Integrations → Stripe and paste the key',
        'Configure refund limits, charge consent rules, and PII redaction',
        'Map AI agents to which Stripe products they can act on',
        'Place a test call to verify a payment link sends and reconciles',
      ],
      authMethods: ['Restricted API key', 'OAuth Connect (multi-account)'],
      syncModes: {
        fullRefresh: true,
        incremental: true,
      },
      requirements: [
        'Stripe account with API access',
        'Restricted API key with appropriate scopes',
        'PCI-aware deployment configuration',
      ],
      notes: [
        'Card numbers are never spoken to the AI; payment links are the default flow',
        'Refund and charge actions are configurable per-agent within policy bounds',
        'PCI-aligned by default; recordings can exclude payment segments',
      ],
    },
    // Salesforce CRM
    {
      slug: 'salesforce-crm',
      name: 'Salesforce CRM',
      category: 'crm' as const,
      description:
        'Read and update Salesforce contacts, accounts, opportunities, and cases live',
      longDescription:
        'Connect Salesforce so the AI can look up contacts, log call activity, update opportunities, fill custom fields, and create cases — in real time during inbound and outbound calls. Bi-directional, schema-aware, and respects your Salesforce profile and field-level security.',
      icon: '☁️',
      logoUrl: '/logos/logo-salesforce.svg',
      color: '#00A1E0',
      features: [
        {
          title: 'Schema-aware updates',
          description:
            "AI reads your Salesforce object schema (including custom fields) and updates records using the field types you've defined.",
        },
        {
          title: 'Activity logging',
          description:
            'Every call, transcript, and outcome logged as an Activity on the Contact, Account, Lead, or Opportunity.',
        },
        {
          title: 'Custom field auto-fill',
          description:
            'AI extracts data from the conversation and fills your custom fields without manual data entry.',
        },
        {
          title: 'Profile-aware security',
          description:
            'AI actions respect Salesforce profiles, permission sets, and field-level security as if it were a regular user.',
        },
      ],
      syncCapabilities: [
        {
          name: 'Contacts',
          description: 'Contact records, related accounts, and history.',
        },
        {
          name: 'Accounts',
          description: 'Account records with health scores and related deals.',
        },
        {
          name: 'Leads',
          description:
            'Lead records with stage, source, and qualification fields.',
        },
        {
          name: 'Opportunities',
          description: 'Pipeline records, stages, amounts, close dates.',
        },
        {
          name: 'Cases',
          description: 'Case records for support and service ticketing.',
        },
      ],
      useCases: [
        'AI sales agent that updates opportunity stages live during calls',
        'Inbound support that creates cases with full transcripts',
        'AI receptionist that logs every call as an Activity automatically',
        'Custom field automation for compliance and data quality',
        'Account-based outreach using Salesforce segments',
      ],
      setupSteps: [
        'In Salesforce, create a Connected App with OAuth scopes for the objects you want',
        'In Open, go to Settings → Integrations → Salesforce and authorize the Connected App',
        'Map Open agent roles to Salesforce profiles for field-level security',
        'Choose which objects and fields the AI can read vs. write',
        'Place a test call and verify the Activity log appears on the right record',
      ],
      authMethods: ['OAuth 2.0', 'JWT Bearer flow (server-to-server)'],
      syncModes: {
        fullRefresh: true,
        incremental: true,
      },
      requirements: [
        'Salesforce edition with API access (Enterprise, Performance, Unlimited, Developer)',
        'Permission to create Connected Apps',
      ],
      notes: [
        'Field-level security is enforced — Open never sees fields the agent profile cannot',
        'Sandboxes are supported for staging and pre-production',
        'Sales Cloud, Service Cloud, and Litify are all supported',
      ],
    },
    // HubSpot CRM
    {
      slug: 'hubspot-crm',
      name: 'HubSpot CRM',
      category: 'crm' as const,
      description:
        'Read and update HubSpot contacts, deals, tickets, and properties live',
      longDescription:
        'Connect HubSpot so the AI can look up contacts, update deal stages, create tickets, fill custom properties, and log call activity in real time. Works across Sales, Service, and Marketing Hubs.',
      icon: '🟧',
      logoUrl: 'https://cdn.simpleicons.org/hubspot/FF7A59',
      color: '#FF7A59',
      features: [
        {
          title: 'Property auto-fill',
          description:
            'AI extracts info from the conversation and updates HubSpot custom properties without manual entry.',
        },
        {
          title: 'Deal stage updates',
          description:
            'AI moves deals through stages based on what the prospect says — qualified, demo booked, lost.',
        },
        {
          title: 'Service Hub tickets',
          description:
            'Inbound calls create tickets with full transcript, intent, and routing to the right team.',
        },
        {
          title: 'Engagement logging',
          description:
            'Every call logged as a HubSpot engagement on the Contact, Company, Deal, or Ticket.',
        },
      ],
      syncCapabilities: [
        {
          name: 'Contacts',
          description:
            'Contact records with all standard and custom properties.',
        },
        {
          name: 'Companies',
          description: 'Company records with associated contacts and deals.',
        },
        {
          name: 'Deals',
          description: 'Pipeline records with stage, amount, and close date.',
        },
        {
          name: 'Tickets',
          description:
            'Service Hub tickets with priority, status, and pipeline.',
        },
        {
          name: 'Engagements',
          description:
            'Call logs, notes, emails, and meetings tied to records.',
        },
      ],
      useCases: [
        'AI BDR that books meetings on the rep calendar with deal updates',
        'Inbound support tickets created from voice calls automatically',
        'Custom property auto-fill for marketing segmentation',
        'AI win-back outreach using HubSpot lists',
        'Renewal calls that update the deal directly',
      ],
      setupSteps: [
        'In HubSpot, create a Private App with the scopes you need (contacts, deals, tickets, etc.)',
        'In Open, go to Settings → Integrations → HubSpot and paste the access token',
        'Map Open agents to HubSpot users for engagement attribution',
        'Choose which objects and properties the AI can read vs. write',
        'Place a test call and verify the engagement appears on the right record',
      ],
      authMethods: ['Private App access token', 'OAuth (marketplace)'],
      syncModes: {
        fullRefresh: true,
        incremental: true,
      },
      requirements: [
        'HubSpot account (any tier with API access)',
        'Super Admin or app-creation permissions',
      ],
      notes: [
        'Sales Hub, Service Hub, and Marketing Hub are all supported',
        'Custom objects (Enterprise tier) supported',
        'Workflow triggers can fire from AI-created engagements',
      ],
    },
    // Pipedrive
    {
      slug: 'pipedrive',
      name: 'Pipedrive',
      category: 'crm' as const,
      description:
        'Sync Pipedrive deals, contacts, and activities with AI calls',
      longDescription:
        "Connect Pipedrive so the AI can update deal stages, log activities, create new deals, and fill custom fields during inbound and outbound calls. Useful for sales-led teams that live in Pipedrive's pipeline view.",
      icon: '📊',
      logoUrl:
        'https://encrypted-tbn0.gstatic.com/images?q=tbn:ANd9GcQKT0_CQJN20uvhZnugIFStoNXXaVB1MuxQxQ&s',
      color: '#1A1A1A',
      features: [
        {
          title: 'Pipeline updates live',
          description:
            'AI moves deals through pipeline stages based on call outcomes.',
        },
        {
          title: 'Activity logging',
          description:
            'Every call logged as a Pipedrive activity with transcript and outcome.',
        },
        {
          title: 'Custom field auto-fill',
          description:
            'AI extracts info and fills your Pipedrive custom fields automatically.',
        },
        {
          title: 'Deal creation from calls',
          description:
            'Inbound calls auto-create deals with the right pipeline and owner.',
        },
      ],
      syncCapabilities: [
        {
          name: 'Deals',
          description: 'Deal records with stage, value, and close date.',
        },
        { name: 'Persons', description: 'Contact records with custom fields.' },
        { name: 'Organizations', description: 'Company-level records.' },
        { name: 'Activities', description: 'Call, meeting, and task logs.' },
      ],
      useCases: [
        'AI SDR that books meetings and updates the deal in real time',
        'Inbound lead qualification with auto deal creation',
        'Outbound follow-up calls on stale deals',
        'Custom field automation for sales reporting',
      ],
      setupSteps: [
        'In Pipedrive, generate a personal API token (Settings → Personal preferences → API)',
        'In Open, go to Settings → Integrations → Pipedrive and paste the token',
        'Map Open agents to Pipedrive users',
        'Configure which pipelines and stages the AI can update',
        'Test with a sample call and verify the activity log',
      ],
      authMethods: ['API token', 'OAuth (marketplace)'],
      syncModes: {
        fullRefresh: true,
        incremental: true,
      },
      requirements: [
        'Pipedrive account with API access',
        'Admin permission for full access',
      ],
      notes: [
        'Multi-pipeline support; AI uses the right pipeline per agent',
        'Custom fields auto-detected from the schema',
      ],
    },
    // Zoho CRM
    {
      slug: 'zoho',
      name: 'Zoho CRM',
      category: 'crm' as const,
      description:
        'Sync Zoho CRM contacts, deals, and activities with AI calls',
      longDescription:
        'Connect Zoho CRM so the AI can read and update contacts, leads, deals, and custom modules during calls. Works across Zoho One and standalone Zoho CRM editions.',
      icon: '🟦',
      logoUrl:
        'https://encrypted-tbn0.gstatic.com/images?q=tbn:ANd9GcSVKaDGgUb-FEMOjw-4bQ1x9v8p2BLCuIU1jQ&s',
      color: '#E42527',
      features: [
        {
          title: 'Lead and deal sync',
          description:
            'Real-time read and write on leads, deals, and accounts.',
        },
        {
          title: 'Custom modules',
          description: 'Native support for Zoho CRM custom modules and fields.',
        },
        {
          title: 'Workflow integration',
          description:
            'AI updates trigger your existing Zoho workflows automatically.',
        },
        {
          title: 'Multi-org support',
          description:
            'Works across Zoho One organizations and standalone CRM accounts.',
        },
      ],
      syncCapabilities: [
        {
          name: 'Leads',
          description: 'Lead records with conversion tracking.',
        },
        {
          name: 'Contacts',
          description: 'Contact records with related accounts.',
        },
        {
          name: 'Accounts',
          description: 'Company-level records and hierarchy.',
        },
        {
          name: 'Deals',
          description: 'Pipeline records with stages and amounts.',
        },
        {
          name: 'Custom modules',
          description: 'Any custom module you have configured.',
        },
      ],
      useCases: [
        'AI sales calls that update leads and deals live',
        'Inbound support tied to Zoho CRM cases',
        'Custom-module updates from voice transcripts',
        'Multi-org rollouts across Zoho One',
      ],
      setupSteps: [
        'In Zoho Developer Console, create a Self Client and generate OAuth credentials',
        'In Open, go to Settings → Integrations → Zoho and authorize the app',
        'Map Open agents to Zoho users',
        'Select which modules and fields the AI can read/write',
        'Test with a sample call',
      ],
      authMethods: ['OAuth 2.0'],
      syncModes: {
        fullRefresh: true,
        incremental: true,
      },
      requirements: [
        'Zoho CRM account with API access',
        'Admin or developer access for OAuth setup',
      ],
      notes: [
        'Region-specific data centers supported (US, EU, IN, AU, JP, CN)',
        'Zoho One bundles supported across modules',
      ],
    },
    // Jira
    {
      slug: 'jira',
      name: 'Jira',
      category: 'it' as const,
      description: 'Create, update, and read Jira issues from AI conversations',
      longDescription:
        "Connect Jira so the AI can create issues, comment on them, update status, and read context — live during support and engineering calls. Works with Jira Cloud, Jira Service Management, and Atlassian's full suite.",
      icon: '🧩',
      logoUrl: 'https://cdn.simpleicons.org/jira/0052CC',
      color: '#0052CC',
      features: [
        {
          title: 'Issue creation from calls',
          description:
            "Support calls create Jira issues with the AI's summary, transcript, and detected priority.",
        },
        {
          title: 'Status and priority updates',
          description:
            'AI moves issues through workflow states based on call outcomes.',
        },
        {
          title: 'JQL-aware lookup',
          description:
            'AI looks up related issues by JQL during a call to give callers status updates.',
        },
        {
          title: 'JSM-native',
          description:
            'Jira Service Management requests, customers, and queues fully supported.',
        },
      ],
      syncCapabilities: [
        { name: 'Issues', description: 'Issue records across all projects.' },
        { name: 'Comments', description: 'Comments with audit trail.' },
        { name: 'Transitions', description: 'Workflow state changes.' },
        {
          name: 'Service Management',
          description: 'JSM requests and customer portals.',
        },
      ],
      useCases: [
        'AI tier-1 support that creates Jira tickets with full context',
        'Inbound JSM calls routed to the right queue automatically',
        'Status update calls that read the latest comment back to the caller',
        'Engineering escalation calls with auto-issue creation',
      ],
      setupSteps: [
        'In Atlassian, create an API token at id.atlassian.com/manage-profile/security/api-tokens',
        'In Open, go to Settings → Integrations → Jira and enter your domain, email, and API token',
        'Map Open agents to Jira users for issue attribution',
        'Configure which projects and issue types the AI can create',
        'Test by creating a sample issue from a test call',
      ],
      authMethods: ['API token + email', 'OAuth 2.0'],
      syncModes: {
        fullRefresh: true,
        incremental: true,
      },
      requirements: [
        'Atlassian Cloud or self-hosted Jira',
        'Project permissions for issue creation and transitions',
      ],
      notes: [
        'Jira Cloud, Jira Server, and Jira Data Center all supported',
        'Custom fields auto-detected from the schema',
        'JSM customer portals integrate with the AI receptionist',
      ],
    },
    // Zapier
    {
      slug: 'zapier',
      name: 'Zapier',
      category: 'automation' as const,
      description:
        "Connect Open to 7,000+ apps via Zapier triggers and actions — when SIP and native APIs aren't enough",
      longDescription:
        "Use Zapier when the integration you need isn't natively supported by Open. Open exposes triggers (call started, call ended, transcript ready, transfer requested) and actions (place outbound call, look up agent, post message) — and Zapier handles the rest, connecting Open to 7,000+ apps.",
      icon: '⚡',
      logoUrl: 'https://cdn.simpleicons.org/zapier/FF4A00',
      color: '#FF4A00',
      features: [
        {
          title: 'Open triggers',
          description:
            'Fire Zaps on call started, call ended, transcript ready, transfer requested, outcome tagged.',
        },
        {
          title: 'Open actions',
          description:
            'Use Zapier to place outbound calls, look up agents, post messages, and update Open from any app.',
        },
        {
          title: '7,000+ apps',
          description:
            "Reach the long tail of business tools that don't have a native Open integration yet.",
        },
        {
          title: 'No-code automation',
          description:
            'Build automations without engineering — your ops team owns the workflow.',
        },
      ],
      syncCapabilities: [
        { name: 'Calls', description: 'Triggers and updates per call.' },
        {
          name: 'Transcripts',
          description: 'Transcript-ready triggers for downstream automation.',
        },
        {
          name: 'Outcomes',
          description:
            'Outcome tags and reasoning traces fire downstream Zaps.',
        },
        {
          name: 'Actions',
          description:
            'Outbound dial, agent lookup, and CRM update via Zapier.',
        },
      ],
      useCases: [
        "Long-tail integrations to apps Open doesn't natively support",
        'No-code workflows owned by ops, not engineering',
        'A/B testing trigger logic across apps',
        'Cross-tool reporting on call outcomes',
      ],
      setupSteps: [
        'In Zapier, search for the Open integration and authorize it',
        'Choose a trigger (call started, transcript ready, outcome) or action (place call, update agent)',
        'Wire the rest of the Zap to your destination app',
        'Test the Zap with a sample event from Open',
        'Turn it on; subsequent events fire the Zap',
      ],
      authMethods: ['OAuth (Zapier marketplace)'],
      syncModes: { fullRefresh: false, incremental: true },
      requirements: [
        'Zapier account on a plan that includes the apps you need',
      ],
      notes: [
        'Native Open integrations are typically faster and lower latency than Zapier',
        'Use native first; reach for Zapier when the destination is not natively supported',
        'No-code, but the trigger/action surface area is the same as the native API',
      ],
    },
    // Webhooks
    {
      slug: 'webhooks',
      name: 'Webhooks',
      category: 'automation' as const,
      description:
        'Stream Open events to any URL — for custom integrations, internal automations, and observability',
      longDescription:
        'When you need direct, low-latency access to Open events from your own infrastructure, webhooks deliver them to any HTTPS URL. Every call event, transcript, transfer, and outcome can be streamed in real time and processed by your own services.',
      icon: '🔗',
      color: '#000000',
      features: [
        {
          title: 'Event-typed payloads',
          description:
            'Each event has a typed payload (call.started, call.ended, transcript.ready, transfer.requested, outcome.tagged).',
        },
        {
          title: 'Signed delivery',
          description:
            'HMAC-signed payloads with replay protection so you can verify origin server-side.',
        },
        {
          title: 'Retries with backoff',
          description:
            'Failed deliveries retry with exponential backoff and a configurable dead-letter queue.',
        },
        {
          title: 'Filter by agent',
          description:
            'Subscribe to events from specific agents only, to keep the firehose manageable.',
        },
      ],
      syncCapabilities: [
        {
          name: 'Call events',
          description: 'call.started, call.ended, call.failed.',
        },
        {
          name: 'Conversation events',
          description: 'transcript.ready, intent.detected, sentiment.scored.',
        },
        {
          name: 'Transfer events',
          description: 'transfer.requested, transfer.completed.',
        },
        { name: 'Outcome events', description: 'outcome.tagged, qa.scored.' },
      ],
      useCases: [
        'Custom CRM integrations not covered by native connectors',
        'Real-time data warehouse ingestion (Snowflake, BigQuery, Redshift)',
        'Observability stacks (Datadog, Grafana, Splunk)',
        'Internal ML pipelines for QA scoring or coaching',
      ],
      setupSteps: [
        'In Open, go to Settings → Integrations → Webhooks',
        'Add an HTTPS endpoint URL',
        'Subscribe to event types and (optionally) filter by agent',
        'Copy the signing secret and verify HMAC signatures server-side',
        'Test with a sample event and confirm receipt',
      ],
      authMethods: [
        'HMAC-signed payloads',
        'Optional mTLS for high-security deployments',
      ],
      syncModes: { fullRefresh: false, incremental: true },
      requirements: [
        'HTTPS endpoint with TLS',
        'Ability to verify HMAC signatures',
      ],
      notes: [
        'Payloads include event metadata, related IDs, and links to fetch full records',
        'Replay events from the dashboard for development and testing',
        'Use the native API for two-way operations; webhooks are one-way notifications',
      ],
    },
    // REST API
    {
      slug: 'rest-api',
      name: 'REST API',
      category: 'automation' as const,
      description:
        "Build any integration the native connectors don't cover with Open's full REST API",
      longDescription:
        'Everything you can do in the Open dashboard is also in the REST API: agents, knowledge, tools, calls, transcripts, transfers, outcomes. Use it for custom integrations, internal tooling, CI-driven prompt rollouts, and programmatic agent management.',
      icon: '🔌',
      logoUrl: 'https://cdn.simpleicons.org/swagger/85EA2D',
      color: '#85EA2D',
      features: [
        {
          title: 'Agent management',
          description: 'Create, version, and roll out agents programmatically.',
        },
        {
          title: 'Call control',
          description:
            'Place outbound calls, fetch transcripts, trigger transfers via API.',
        },
        {
          title: 'Knowledge sync',
          description:
            'Push and pull knowledge-base content to keep agents current.',
        },
        {
          title: 'Reporting',
          description:
            'Pull outcome tags, reasoning traces, and analytics for your own reporting.',
        },
      ],
      syncCapabilities: [
        {
          name: 'Agents',
          description: 'Full CRUD on agent configuration and versions.',
        },
        {
          name: 'Knowledge',
          description: 'Knowledge base entries, retrieval traces.',
        },
        {
          name: 'Calls',
          description: 'Call records, transcripts, recordings.',
        },
        {
          name: 'Tools',
          description: 'Tool definitions and per-agent tool grants.',
        },
        {
          name: 'Outcomes',
          description: 'Outcome tags, QA scores, reasoning traces.',
        },
      ],
      useCases: [
        'CI/CD-driven prompt rollouts',
        'Custom dashboards and reporting',
        'Programmatic agent provisioning for multi-tenant deployments',
        'Internal tooling for prompt engineers and QA leads',
      ],
      setupSteps: [
        'In Open, go to Settings → API Keys and create a key with the right scopes',
        'Browse the OpenAPI spec at docs.open.cx/api-reference',
        'Set up your client (curl, Postman, or your language SDK)',
        'Make your first call: GET /agents to list configured agents',
      ],
      authMethods: ['Bearer API key', 'Optional mTLS for enterprise'],
      syncModes: { fullRefresh: true, incremental: true },
      requirements: ['Open account', 'API key with the required scopes'],
      notes: [
        'OpenAPI 3 spec available; SDKs in TypeScript, Python, and Go',
        'Rate limits scale with subscription tier; enterprise plans get higher limits',
        'Test mode supported — calls placed in test mode do not charge or hit real PSTN',
      ],
    },
    // Web Crawler
    {
      slug: 'web-crawler',
      name: 'Web Crawler',
      category: 'training' as const,
      description:
        'Train your AI agent on your public website — docs, help center, blog, marketing pages',
      longDescription:
        "Open's web crawler indexes any public website you point it at — your docs site, help center, blog, knowledge base, marketing pages — and feeds the content into your AI agent's knowledge. Recrawls on a schedule so the agent stays current.",
      icon: '🌐',
      logoUrl: 'https://cdn.simpleicons.org/googlechrome/4285F4',
      color: '#4285F4',
      features: [
        {
          title: 'Sitemap-aware',
          description:
            'Reads sitemap.xml when present and follows discovered links recursively.',
        },
        {
          title: 'Smart re-crawl',
          description:
            'Recrawls on a schedule (daily/weekly/monthly) and only updates pages that changed.',
        },
        {
          title: 'Selector-based extraction',
          description:
            'Configure CSS selectors to skip nav, footer, and ads — only the content the agent needs.',
        },
        {
          title: 'JS rendering',
          description:
            'Renders JavaScript-heavy SPAs so client-rendered content gets indexed too.',
        },
      ],
      syncCapabilities: [
        {
          name: 'Pages',
          description: 'All discoverable pages with full HTML extraction.',
        },
        { name: 'Sitemaps', description: 'sitemap.xml-based discovery.' },
        {
          name: 'Selectors',
          description: 'CSS-selector-based content extraction.',
        },
      ],
      useCases: [
        'Public docs sites (Docusaurus, Mintlify, GitBook, custom)',
        'Help centers (Zendesk, Intercom, Freshdesk on a custom domain)',
        'Marketing sites and product pages',
        'Blogs and changelog pages',
      ],
      setupSteps: [
        'In Open, go to AI Training → Sources → Add Web Crawler',
        'Enter the seed URL and (optionally) a sitemap URL',
        'Configure selectors to include / exclude (nav, footer, ads)',
        'Set the recrawl schedule',
        'Run the first crawl and review the indexed content',
      ],
      authMethods: [
        'Public crawling (no auth)',
        'Basic auth for protected sites',
      ],
      syncModes: { fullRefresh: true, incremental: true },
      requirements: ['Public or basic-auth-protected website'],
      notes: [
        'Respects robots.txt by default; can be overridden for sites you own',
        'JS-heavy SPAs supported via headless rendering',
        'Per-domain rate limits keep crawls polite',
      ],
    },
    // Twilio
    {
      slug: 'twilio',
      name: 'Twilio',
      category: 'it' as const,
      description:
        "Use your existing Twilio numbers, trunks, and voice infrastructure with Open's AI voice agent",
      longDescription:
        'Open registers as a SIP destination on your existing Twilio Voice account so your numbers, your trunks, and your existing call recording, billing, and reporting all stay where they are. The AI just answers the calls. Inbound and outbound supported, including programmable voice flows that hand off to Open mid-call.',
      icon: '📞',
      logoUrl: '/telephony/twilio.svg',
      color: '#F22F46',
      features: [
        {
          title: 'Numbers stay on Twilio',
          description:
            'Inbound forwarding via SIP INVITE; no porting, no migration.',
        },
        {
          title: 'Outbound originates on your trunk',
          description:
            'Open dials through your Twilio Elastic SIP Trunk so your existing per-minute rates apply.',
        },
        {
          title: 'TwiML hand-off',
          description:
            'Programmable voice flows can hand off to Open mid-call via <Sip> verb when AI is the right next step.',
        },
        {
          title: 'Conversation Intelligence parity',
          description:
            'Twilio recordings and Open transcripts can both be retained, with PII redaction available on both sides.',
        },
      ],
      syncCapabilities: [
        {
          name: 'Inbound calls',
          description:
            'SIP INVITE forwarded to Open; AI answers and runs the conversation.',
        },
        {
          name: 'Outbound calls',
          description:
            'Open originates outbound calls through your Twilio Elastic SIP Trunk.',
        },
        {
          name: 'Recording',
          description:
            'Twilio call recording continues unchanged; Open captures its own structured transcripts.',
        },
        {
          name: 'TwiML',
          description:
            'Programmable voice flows can hand off to Open with the <Sip> or <Refer> verb.',
        },
      ],
      useCases: [
        'AI voice agent on your existing Twilio numbers without porting',
        'Outbound campaigns originated through your Twilio trunk',
        'Programmable voice flows that hand off to AI when conditions are met',
        'Hybrid IVR + AI deployments with TwiML controlling the routing',
      ],
      setupSteps: [
        'In Twilio Console, identify the SIP Domain or Elastic SIP Trunk you want to use',
        'In Open, go to Settings → Integrations → Twilio and provide the SIP Domain or Trunk SID',
        'Configure the inbound TwiML or trunk routing to forward to Open',
        'For outbound, authorize Open to originate calls through your trunk',
        'Place a test call and verify the trunk routing in Twilio Console + Open dashboard',
      ],
      authMethods: [
        'Account SID + Auth Token',
        'API key with restricted permissions',
      ],
      syncModes: { fullRefresh: false, incremental: true },
      requirements: [
        'Active Twilio account with Voice or Programmable Voice',
        'Elastic SIP Trunk for outbound (recommended)',
        'Account SID and Auth Token or restricted API Key',
      ],
      notes: [
        'Open does not bill carrier minutes — those stay on your Twilio invoice',
        'Twilio recordings and Open transcripts are both retained per your config',
        'Works with Twilio Voice, Programmable Voice, Elastic SIP Trunking, and Flex',
      ],
    },
    // Google Calendar
    {
      slug: 'google-calendar',
      name: 'Google Calendar',
      category: 'automation' as const,
      description:
        'Live read and write to Google Calendar — book, reschedule, and cancel during the call',
      longDescription:
        'Connect Google Calendar so the AI can check live availability, book appointments, reschedule, and cancel — all within the call. Works with personal Google accounts and Google Workspace, supports multi-calendar lookups for service-provider routing, respects your existing busy/free privacy settings.',
      icon: '📅',
      logoUrl: 'https://cdn.simpleicons.org/googlecalendar/4285F4',
      color: '#4285F4',
      features: [
        {
          title: 'Live availability',
          description:
            "Real-time free/busy lookup against the right calendar — never offers a slot that isn't actually available.",
        },
        {
          title: 'Multi-calendar booking',
          description:
            "Service-provider routing — books on the dentist, attorney, or technician's specific calendar by name or skill.",
        },
        {
          title: 'Reschedule and cancel',
          description:
            'Existing event lookup by phone number; reschedule or cancel during the call without manual handoff.',
        },
        {
          title: 'Workspace and personal',
          description:
            'Works with Google Workspace domains and personal Google accounts. Domain-wide delegation supported for org-level deploy.',
        },
      ],
      syncCapabilities: [
        {
          name: 'Events',
          description: 'Read, create, update, and delete calendar events.',
        },
        {
          name: 'Free/busy',
          description: 'Live availability checks against multiple calendars.',
        },
        {
          name: 'Calendars',
          description: 'List and select calendars per agent or per service.',
        },
        {
          name: 'Attendees',
          description: 'Add invitees, send invites, manage RSVPs.',
        },
      ],
      useCases: [
        'AI receptionist booking patient appointments live',
        'Real-estate AI booking showings on the listing-agent calendar',
        'Sales-AI booking demos on the AE calendar with round-robin routing',
        'After-hours rescheduling without front-desk involvement',
      ],
      setupSteps: [
        'In Open, go to Settings → Integrations → Google Calendar',
        'Authorize via OAuth (per-user or domain-wide for Workspace)',
        'Select the calendars the agent can read from and write to',
        'Configure routing rules (which calendar for which service)',
        'Place a test booking and verify it appears in Calendar',
      ],
      authMethods: [
        'OAuth 2.0 (recommended)',
        'Service account with domain-wide delegation',
      ],
      syncModes: { fullRefresh: true, incremental: true },
      requirements: [
        'Google account or Google Workspace domain',
        'Calendar API enabled',
        'For Workspace: super-admin to grant domain-wide delegation (optional)',
      ],
      notes: [
        'Domain-wide delegation lets one Open install serve all users in a Workspace',
        'Free/busy privacy settings respected — Open sees only what the calendar share allows',
        'Booking confirmations sent through your existing Calendar invite flow',
      ],
    },
    // Microsoft 365 Calendar / Outlook
    {
      slug: 'microsoft-365-calendar',
      name: 'Microsoft 365 Calendar',
      category: 'automation' as const,
      description:
        'Live read and write to Outlook / Microsoft 365 Calendar — book, reschedule, and cancel during the call',
      longDescription:
        'Connect Microsoft 365 Calendar (Outlook on Microsoft 365 / Office 365) so the AI can check live availability, book, reschedule, and cancel during the call. Works with single-tenant and multi-tenant Microsoft Entra (Azure AD) configs, supports application-permission flow for org-level deploy.',
      icon: '📆',
      color: '#0078D4',
      features: [
        {
          title: 'Live availability via Microsoft Graph',
          description: 'Real-time free/busy lookup against the right calendar.',
        },
        {
          title: 'Tenant-wide deploy',
          description:
            'Application permissions through Microsoft Entra ID for one install serving every user in your tenant.',
        },
        {
          title: 'Reschedule and cancel',
          description:
            'Lookup by phone number, reschedule or cancel during the call. Teams meeting links generated where applicable.',
        },
        {
          title: 'Shared mailboxes & resources',
          description:
            'Books on shared mailboxes, conference rooms, and bookable resources via Graph.',
        },
      ],
      syncCapabilities: [
        {
          name: 'Events',
          description:
            'Read, create, update, delete events via Microsoft Graph.',
        },
        {
          name: 'Free/busy',
          description: 'Live availability against user and resource calendars.',
        },
        {
          name: 'Calendars',
          description: 'List and select user calendars and shared mailboxes.',
        },
        {
          name: 'Teams meetings',
          description:
            'Optional Teams meeting link auto-generated on booked events.',
        },
      ],
      useCases: [
        'Enterprise AI receptionist booking on Outlook/Teams calendars',
        'Resource-room booking on shared calendars',
        'Tenant-wide deployment serving every Microsoft 365 user',
        'Hybrid Outlook + Google environments through a single agent',
      ],
      setupSteps: [
        'In Microsoft Entra ID, register an app for Open (or use the Open marketplace app)',
        'Grant Calendar.ReadWrite (delegated or application) permissions',
        'In Open, go to Settings → Integrations → Microsoft 365 Calendar and authorize',
        'Choose delegated (per-user) or application (tenant-wide) permission flow',
        'Place a test booking and verify it appears in the user calendar',
      ],
      authMethods: [
        'OAuth 2.0 delegated',
        'Application permissions (client credentials)',
      ],
      syncModes: { fullRefresh: true, incremental: true },
      requirements: [
        'Microsoft 365 / Office 365 with Calendar enabled',
        'Microsoft Entra ID (Azure AD) admin to grant app permissions',
      ],
      notes: [
        'Application permissions allow one Open install to serve every user in the tenant',
        'Conditional Access and MFA respected — Open follows your existing Entra policies',
        'Sovereign clouds (GCC High, GCC, DoD) supported on enterprise plans',
      ],
    },
    // Calendly
    {
      slug: 'calendly',
      name: 'Calendly',
      category: 'automation' as const,
      description:
        "Use your existing Calendly event types as the AI's booking surface during calls",
      longDescription:
        'Connect Calendly so the AI uses your existing event types (15-min intro, 30-min discovery, 60-min consult) as the booking surface during voice calls. Round-robin assignment, working hours, buffer rules, and post-booking automations all continue to work.',
      icon: '🗓️',
      logoUrl: 'https://cdn.simpleicons.org/calendly/006BFF',
      color: '#006BFF',
      features: [
        {
          title: 'Existing event types',
          description:
            'AI books on the event types you already configured — no parallel calendar to maintain.',
        },
        {
          title: 'Round-robin and assignment',
          description:
            "Calendly's existing routing logic applies — round-robin, geo-based, account-owner — same rules as the form.",
        },
        {
          title: 'Working hours and buffers',
          description:
            'AI respects your event-type working hours, minimum-notice, max-per-day, and buffer rules.',
        },
        {
          title: 'Post-booking workflows continue',
          description:
            'Calendly Workflows (reminders, post-meeting follow-ups, CRM sync) trigger normally on AI-booked events.',
        },
      ],
      syncCapabilities: [
        {
          name: 'Event types',
          description: 'List and present available event types per agent.',
        },
        {
          name: 'Bookings',
          description:
            'Create bookings via Calendly API on the right event type.',
        },
        {
          name: 'Hosts',
          description: 'Round-robin and assigned-host routing supported.',
        },
        {
          name: 'Cancellations',
          description: 'Reschedule and cancel via the same API.',
        },
      ],
      useCases: [
        'AI SDR booking discovery calls on the right AE via Calendly round-robin',
        'AI receptionist booking consultations using existing Calendly event types',
        'Hybrid form + voice booking with the same event types and same outcomes',
      ],
      setupSteps: [
        'In Calendly, generate a personal access token (or OAuth app for org install)',
        'In Open, go to Settings → Integrations → Calendly and paste the token',
        'Select which event types each agent can book',
        'Configure assignment rules (round-robin honoured automatically)',
        'Place a test call and verify the booking appears in Calendly',
      ],
      authMethods: [
        'Personal access token',
        'OAuth 2.0 for organization-wide install',
      ],
      syncModes: { fullRefresh: true, incremental: true },
      requirements: [
        'Calendly account (Standard, Teams, or Enterprise)',
        'Personal access token or OAuth admin for org install',
      ],
      notes: [
        'Calendly Workflows continue to fire on AI-booked events',
        'Round-robin counters update normally',
        'Free Calendly accounts work but lack some routing features',
      ],
    },
    // WhatsApp Business
    {
      slug: 'whatsapp-business',
      name: 'WhatsApp Business',
      category: 'automation' as const,
      description:
        'AI agent on WhatsApp Business — same brain as your voice agent, native to the channel',
      longDescription:
        'Connect WhatsApp Business via the official Cloud API or a BSP (Twilio, Vonage, Infobip, MessageBird) so the same AI brain that runs your voice agent also handles WhatsApp inbound. One knowledge base, one tool layer, one observability stack across both channels.',
      icon: '💬',
      logoUrl: 'https://cdn.simpleicons.org/whatsapp/25D366',
      color: '#25D366',
      features: [
        {
          title: 'Cloud API or BSP',
          description:
            'Direct Meta Cloud API or via Twilio, Vonage, Infobip, MessageBird, 360dialog.',
        },
        {
          title: 'Template-message-aware',
          description:
            'AI selects the right approved template for outbound notifications; freeform within the 24-hour window.',
        },
        {
          title: 'Same brain across channels',
          description:
            'The same AI runtime handles voice and WhatsApp; consistent answers, consistent escalation, one inbox for human takeover.',
        },
        {
          title: 'Media-aware',
          description:
            'Receives images, voice notes, PDFs, location pins; replies with rich messages where supported.',
        },
      ],
      syncCapabilities: [
        {
          name: 'Messages',
          description: 'Inbound and outbound text, media, voice notes.',
        },
        {
          name: 'Templates',
          description:
            'Approved template messages used for notifications outside the 24-hour window.',
        },
        {
          name: 'Contacts',
          description: 'WhatsApp contacts synced to your CRM via phone match.',
        },
        {
          name: 'Status updates',
          description:
            'Delivery, read receipts, opt-outs propagated to your CRM.',
        },
      ],
      useCases: [
        'WhatsApp-first customer service in BR, IN, MX, ID, ZA, NG markets',
        'Order status, delivery updates, payment links over WhatsApp Business',
        'Lead qualification via WhatsApp before voice handoff',
        'Hybrid voice + WhatsApp campaigns with the same agent',
      ],
      setupSteps: [
        'Register a WhatsApp Business Account via Meta Business Manager (or your BSP)',
        'Verify your business and configure phone numbers',
        'In Open, go to Settings → Integrations → WhatsApp Business',
        'Provide the Cloud API access token or BSP credentials',
        'Submit message templates for the outbound notifications you need',
        'Verify inbound webhook by sending a test message',
      ],
      authMethods: [
        'Meta Cloud API (system user access token)',
        'BSP credentials (Twilio, Vonage, Infobip, MessageBird, 360dialog)',
      ],
      syncModes: { fullRefresh: false, incremental: true },
      requirements: [
        'WhatsApp Business Account (verified)',
        'Phone number registered to the WABA',
        'Approved message templates for outbound notifications',
      ],
      notes: [
        'Direct Cloud API is the cheapest path; BSPs add value with provisioning, billing consolidation, and template review',
        '24-hour conversation window applies to freeform replies; outside that window only approved templates can be sent',
        'Brazil, India, Mexico, Indonesia, South Africa, Nigeria are top WhatsApp-first markets',
      ],
    },
    // OpenDental
    {
      slug: 'opendental',
      name: 'OpenDental',
      category: 'crm' as const,
      description:
        'Native OpenDental integration — book appointments, run eligibility, capture recall, all live during the call',
      longDescription:
        'Connect OpenDental so the AI can identify patients by phone number, look up upcoming appointments, book in the live schedule against the right provider and operatory, run insurance eligibility, capture recalls, and trigger reactivation campaigns. Works with both OpenDental Cloud and on-prem deployments via the OpenDental Service / FHIR / API bridge.',
      icon: '🦷',
      logoUrl: 'https://cdn.simpleicons.org/databricks/000000',
      color: '#1B5E97',
      features: [
        {
          title: 'Live schedule booking',
          description:
            'Books in the right operatory with the right provider and right service code.',
        },
        {
          title: 'Insurance eligibility',
          description:
            'Pulls eligibility and benefits via your OpenDental clearinghouse setup.',
        },
        {
          title: 'Recall and reactivation',
          description:
            'Outbound calls trigger from OpenDental recall queries; outcomes log back to the chart.',
        },
        {
          title: 'Provider-specific routing',
          description:
            'DDS, hygiene, specialist, and operatory matching baked in.',
        },
      ],
      syncCapabilities: [
        {
          name: 'Patients',
          description: 'Patient lookup by phone, name, and DOB.',
        },
        {
          name: 'Appointments',
          description:
            'Book, reschedule, and cancel against the live schedule.',
        },
        {
          name: 'Operatories',
          description: 'Operatory and provider matching during booking.',
        },
        {
          name: 'Recalls',
          description: 'Outbound calls driven by OpenDental recall queries.',
        },
        {
          name: 'Eligibility',
          description: 'Insurance eligibility and benefits checks.',
        },
      ],
      useCases: [
        'AI dental receptionist for solo practices and DSOs',
        'Recall reactivation campaigns for hygiene appointments',
        'After-hours emergency triage with on-call DDS escalation',
        'Insurance verification during the booking call',
      ],
      setupSteps: [
        'Confirm your OpenDental version supports the API/Service bridge (24.x and later recommended)',
        'In OpenDental, generate an API key with the required permissions',
        'In Open, go to Settings → Integrations → OpenDental and provide the API key plus your server URL',
        'Map providers and operatories to Open agent routing rules',
        'Place a test booking and verify it appears in the OpenDental schedule',
      ],
      authMethods: ['API key (HTTP)', 'OpenDental Service bridge'],
      syncModes: { fullRefresh: true, incremental: true },
      requirements: [
        'OpenDental 24.x or later',
        'Network access between Open and your OpenDental server',
      ],
      notes: [
        'Cloud OpenDental and on-prem both supported',
        'Patient PHI handled under HIPAA-aligned BAA',
        'Custom field mapping supported for practice-specific data',
      ],
    },
    // Dentrix
    {
      slug: 'dentrix',
      name: 'Dentrix',
      category: 'crm' as const,
      description:
        'Native Dentrix integration — book in the live schedule, run eligibility, manage recalls.',
      longDescription:
        'Connect Dentrix (G7+) and Dentrix Ascend so the AI can identify patients by phone, book in the live appointment book, run insurance eligibility, and manage recall campaigns. Works with both on-prem Dentrix and the cloud-based Dentrix Ascend.',
      icon: '🦷',
      logoUrl: 'https://cdn.simpleicons.org/databricks/F37A20',
      color: '#F37A20',
      features: [
        {
          title: 'Live appointment book',
          description:
            'Books against the right operatory, right provider, right procedure code.',
        },
        {
          title: 'Eligibility checks',
          description:
            'Insurance verification through Dentrix eClaims integrations.',
        },
        {
          title: 'Recall management',
          description:
            'Outbound campaigns from Dentrix recall queues; outcomes log to the chart.',
        },
        {
          title: 'Multi-location',
          description:
            'Multi-location practices and group DSOs supported with location-specific routing.',
        },
      ],
      syncCapabilities: [
        { name: 'Patients', description: 'Lookup by phone, name, DOB.' },
        { name: 'Appointments', description: 'Book, reschedule, cancel.' },
        { name: 'Procedures', description: 'Procedure-code-aware booking.' },
        { name: 'Recalls', description: 'Outbound recall campaigns.' },
      ],
      useCases: [
        'AI dental receptionist on Dentrix or Dentrix Ascend',
        'Recall and reactivation outbound',
        'Group-practice multi-location call centre',
        'Eligibility verification at booking time',
      ],
      setupSteps: [
        'In Dentrix, enable the Dentrix API or use the Dentrix Connect bridge',
        'Generate an API key with appointment, patient, and recall scopes',
        'In Open, go to Settings → Integrations → Dentrix and provide the credentials',
        'Map providers, operatories, and locations to Open routing rules',
        'Test a booking end-to-end',
      ],
      authMethods: ['API key', 'Dentrix Connect bridge'],
      syncModes: { fullRefresh: true, incremental: true },
      requirements: [
        'Dentrix G7+ or Dentrix Ascend',
        'Network connectivity between Open and Dentrix server (on-prem)',
      ],
      notes: [
        'Both Dentrix on-prem and Dentrix Ascend supported',
        'HIPAA-aligned BAA available',
        'Group/DSO multi-location supported with separate agent routing per location',
      ],
    },
    // Eaglesoft
    {
      slug: 'eaglesoft',
      name: 'Eaglesoft',
      category: 'crm' as const,
      description:
        'Native Eaglesoft integration — appointment booking, eligibility, recall management.',
      longDescription:
        "Connect Eaglesoft (Patterson Dental's practice-management system) so the AI can book in the live schedule, run insurance eligibility, and manage recall and reactivation. Works with the Patterson Eaglesoft API bridge.",
      icon: '🦷',
      logoUrl: 'https://cdn.simpleicons.org/databricks/0070BC',
      color: '#0070BC',
      features: [
        {
          title: 'Live schedule booking',
          description:
            'Operatory and provider-aware booking through the Eaglesoft API.',
        },
        {
          title: 'Eligibility',
          description: 'Insurance eligibility checks via Eaglesoft eServices.',
        },
        {
          title: 'Recalls',
          description:
            'Outbound recall calls driven by Eaglesoft recall queries.',
        },
        {
          title: 'Charting fields',
          description: 'Custom chart-field updates from call data.',
        },
      ],
      syncCapabilities: [
        { name: 'Patients', description: 'Lookup and identification.' },
        { name: 'Appointments', description: 'Book, reschedule, cancel.' },
        {
          name: 'Recalls',
          description: 'Outbound recall campaign management.',
        },
        {
          name: 'Procedures',
          description: 'Procedure-code lookup for proper booking.',
        },
      ],
      useCases: [
        'AI dental receptionist on Eaglesoft',
        'Recall outbound for hygiene appointments',
        'New-patient intake with insurance verification',
      ],
      setupSteps: [
        'In Eaglesoft, enable the Patterson API bridge',
        'Generate API credentials with appointment and patient scopes',
        'In Open, go to Settings → Integrations → Eaglesoft and provide credentials',
        'Map providers and operatories',
        'Test a booking',
      ],
      authMethods: ['Patterson Eaglesoft API key'],
      syncModes: { fullRefresh: true, incremental: true },
      requirements: [
        'Eaglesoft 19+ with API bridge enabled',
        'Network connectivity between Open and the Eaglesoft server',
      ],
      notes: [
        'HIPAA-aligned BAA available',
        'On-prem Eaglesoft deployments supported',
      ],
    },
    // ServiceTitan
    {
      slug: 'servicetitan',
      name: 'ServiceTitan',
      category: 'crm' as const,
      description:
        'Native ServiceTitan integration — book service calls in the live dispatch board, quote from the rate card, manage on-call escalation.',
      longDescription:
        'Connect ServiceTitan so the AI can book service calls in the live dispatch board, quote dispatch fees from your rate card, run zone validation, and manage on-call escalation for HVAC, plumbing, electrical, roofing, and pest. The ServiceTitan API supports business-unit routing, skill-based dispatching, and revenue tracking.',
      icon: '🔧',
      logoUrl: 'https://cdn.simpleicons.org/databricks/3DC6F0',
      color: '#3DC6F0',
      features: [
        {
          title: 'Live dispatch board',
          description:
            'Books in the right business unit, zone, and tech-skill match.',
        },
        {
          title: 'Rate-card pricing',
          description:
            'Reads your pricebook to quote regular/after-hours/emergency rates accurately.',
        },
        {
          title: 'Zone validation',
          description:
            'Address validation against your service zones, with referral logic for out-of-zone.',
        },
        {
          title: 'On-call escalation',
          description:
            'Pages the right on-call tech via SMS or warm transfer with the live transcript.',
        },
      ],
      syncCapabilities: [
        {
          name: 'Customers',
          description: 'Lookup by phone, address, account.',
        },
        { name: 'Jobs', description: 'Create, reschedule, cancel.' },
        {
          name: 'Pricebook',
          description: 'Service codes, rates, dispatch fees.',
        },
        {
          name: 'Technicians',
          description: 'Skill-based and zone-based dispatch.',
        },
        {
          name: 'Business units',
          description:
            'Multi-trade businesses (HVAC + plumbing) routed per unit.',
        },
      ],
      useCases: [
        'AI receptionist for HVAC, plumbing, electrical, roofing, pest',
        'After-hours emergency triage with on-call paging',
        'Multi-trade businesses with one number per brand or shared',
        'Outbound campaign on stale leads or missed maintenance',
      ],
      setupSteps: [
        'In ServiceTitan, enable the API and create an API integration',
        'Note the Tenant ID, Client ID, and Client Secret',
        'In Open, go to Settings → Integrations → ServiceTitan and provide the credentials',
        'Map business units, zones, and pricebook items to Open agent rules',
        'Test a booking and verify it appears in the dispatch board',
      ],
      authMethods: ['OAuth 2.0 (Client Credentials)', 'API integration token'],
      syncModes: { fullRefresh: true, incremental: true },
      requirements: [
        'ServiceTitan tenant with API access',
        'API integration credentials',
      ],
      notes: [
        'Multi-business-unit (HVAC + plumbing + electrical) supported in one Open install',
        'Pricebook updates sync automatically',
        'Custom job-form fields supported',
      ],
    },
    // Housecall Pro
    {
      slug: 'housecall-pro',
      name: 'Housecall Pro',
      category: 'crm' as const,
      description:
        'Native Housecall Pro integration — book jobs in the live schedule, manage dispatch.',
      longDescription:
        'Connect Housecall Pro so the AI can book jobs in your live schedule, manage dispatch, run estimate-to-job conversion, and trigger outbound on missed appointments. Works with single-location and multi-location franchises.',
      icon: '🛠️',
      logoUrl: 'https://cdn.simpleicons.org/databricks/00B388',
      color: '#00B388',
      features: [
        {
          title: 'Live schedule booking',
          description:
            'Books against the right tech with skill and zone matching.',
        },
        {
          title: 'Estimate to job',
          description:
            'Converts approved estimates to scheduled jobs during the call.',
        },
        {
          title: 'Outbound on missed',
          description:
            'Triggers outbound for missed appointments and stale estimates.',
        },
        {
          title: 'Mobile-friendly handoff',
          description:
            'Tech sees the AI-booked job and intake details on the mobile app.',
        },
      ],
      syncCapabilities: [
        { name: 'Customers', description: 'Lookup and update.' },
        { name: 'Jobs', description: 'Schedule, reschedule, cancel.' },
        { name: 'Estimates', description: 'Convert estimates to jobs.' },
        { name: 'Technicians', description: 'Skill and zone-based dispatch.' },
      ],
      useCases: [
        'AI receptionist for SMB home-services using Housecall Pro',
        'Outbound on stale estimates',
        'After-hours overflow into the next-day schedule',
      ],
      setupSteps: [
        'In Housecall Pro, generate an API key (Settings → Integrations → API)',
        'In Open, go to Settings → Integrations → Housecall Pro and provide the key',
        'Map technicians, zones, and service types',
        'Test a job creation end-to-end',
      ],
      authMethods: ['API key', 'OAuth 2.0 (in development)'],
      syncModes: { fullRefresh: true, incremental: true },
      requirements: ['Housecall Pro Plus or Premium plan with API access'],
      notes: [
        'Mobile app reflects AI-booked jobs in real time',
        'Multi-location franchises supported with per-location agents',
      ],
    },
    // Jobber
    {
      slug: 'jobber',
      name: 'Jobber',
      category: 'crm' as const,
      description:
        'Native Jobber integration — book jobs, manage estimates, run outbound campaigns.',
      longDescription:
        'Connect Jobber so the AI can book jobs in the live schedule, manage estimate-to-job conversion, and run outbound campaigns. Works for landscaping, cleaning, HVAC, plumbing, and other Jobber-served verticals.',
      icon: '🌿',
      logoUrl: 'https://cdn.simpleicons.org/databricks/4CAF50',
      color: '#4CAF50',
      features: [
        {
          title: 'Live schedule booking',
          description: 'Skill, zone, and crew-based dispatch.',
        },
        {
          title: 'Recurring jobs',
          description:
            'Books recurring service jobs (lawn, cleaning, pest) on the right cadence.',
        },
        {
          title: 'Estimate to job',
          description: 'Converts approved estimates to scheduled jobs.',
        },
        {
          title: 'Client communication',
          description: 'Sends Jobber-native confirmations and reminders.',
        },
      ],
      syncCapabilities: [
        { name: 'Clients', description: 'Lookup, create, update.' },
        {
          name: 'Jobs',
          description:
            'Schedule, reschedule, cancel; recurring jobs supported.',
        },
        { name: 'Quotes', description: 'Quote-to-job conversion.' },
        { name: 'Crews', description: 'Crew-based dispatch.' },
      ],
      useCases: [
        'AI receptionist for landscaping, cleaning, lawn care',
        'Recurring-service booking and rescheduling',
        'Outbound on stale quotes',
      ],
      setupSteps: [
        'In Jobber, generate an API key (Settings → API)',
        'In Open, go to Settings → Integrations → Jobber and provide the key',
        'Map crews, zones, and service types',
        'Test a job creation',
      ],
      authMethods: ['API key', 'OAuth 2.0'],
      syncModes: { fullRefresh: true, incremental: true },
      requirements: ['Jobber Connect or Grow plan with API access'],
      notes: [
        'Recurring-service businesses get full schedule-aware booking',
        'Jobber Workflows fire on AI-booked jobs',
      ],
    },
    // Square
    {
      slug: 'square',
      name: 'Square',
      category: 'ecommerce' as const,
      description:
        'Square integration — appointments, payments, customers, and POS data.',
      longDescription:
        'Connect Square so the AI can book Square Appointments, take payments via payment links, look up customers in Square Customer Directory, and reference Square POS sales history. Works for salons, spas, retail, restaurants, and any Square Point of Sale customer.',
      icon: '◼️',
      logoUrl: 'https://cdn.simpleicons.org/square/000000',
      color: '#000000',
      features: [
        {
          title: 'Square Appointments booking',
          description:
            'Books on the right service-provider with service-duration awareness.',
        },
        {
          title: 'Payment links',
          description:
            'Sends Square-hosted payment links via SMS during the call.',
        },
        {
          title: 'Customer Directory',
          description: 'Lookup by phone or email; updates customer profile.',
        },
        {
          title: 'POS-aware',
          description:
            'References past sales history for personalised conversations.',
        },
      ],
      syncCapabilities: [
        {
          name: 'Customers',
          description: 'Lookup, create, update in Customer Directory.',
        },
        { name: 'Bookings', description: 'Square Appointments scheduling.' },
        { name: 'Orders', description: 'Read order history and status.' },
        {
          name: 'Payments',
          description: 'Send payment links, verify payment status.',
        },
      ],
      useCases: [
        'AI receptionist for salons, spas, and beauty businesses on Square Appointments',
        'Restaurant reservation and order management',
        'Retail customer service with order history',
        'Payment collection over voice via payment links',
      ],
      setupSteps: [
        'In Square Developer dashboard, create an application or use Square OAuth',
        'In Open, go to Settings → Integrations → Square and authorize',
        'Map service providers and service types',
        'Test a booking',
      ],
      authMethods: ['OAuth 2.0', 'Personal access token (testing only)'],
      syncModes: { fullRefresh: true, incremental: true },
      requirements: [
        'Square Seller account',
        'Square Appointments / POS / Online enabled as needed',
      ],
      notes: [
        'Multi-location Square businesses supported',
        'PCI-compliant payment-link flow',
      ],
    },
    // Acuity Scheduling
    {
      slug: 'acuity-scheduling',
      name: 'Acuity Scheduling',
      category: 'automation' as const,
      description:
        'Acuity Scheduling integration — book appointments live during voice calls.',
      longDescription:
        'Connect Acuity Scheduling (Squarespace Scheduling) so the AI uses your existing appointment types as the booking surface during voice calls. Working hours, custom forms, and post-booking automations all continue to fire.',
      icon: '⏰',
      logoUrl: 'https://cdn.simpleicons.org/databricks/000000',
      color: '#FF8C00',
      features: [
        {
          title: 'Existing appointment types',
          description:
            'Uses your Acuity appointment types — no parallel scheduling system.',
        },
        {
          title: 'Working hours and limits',
          description: 'Acuity working hours, lead time, and limits respected.',
        },
        {
          title: 'Custom forms',
          description:
            'AI captures custom-form fields during the conversation.',
        },
        {
          title: 'Multi-calendar',
          description:
            'Multi-calendar Acuity accounts supported with provider routing.',
        },
      ],
      syncCapabilities: [
        {
          name: 'Appointment types',
          description: 'List and present types per agent.',
        },
        { name: 'Bookings', description: 'Create, reschedule, cancel.' },
        { name: 'Calendars', description: 'Per-provider calendar routing.' },
        {
          name: 'Custom forms',
          description: 'Capture form data during the call.',
        },
      ],
      useCases: [
        'Therapy, coaching, and consulting practices on Acuity',
        'Multi-provider service businesses with provider-specific calendars',
        'AI booking with existing custom intake forms',
      ],
      setupSteps: [
        'In Acuity, generate an API user/key (Account → Integrations → API)',
        'In Open, go to Settings → Integrations → Acuity and provide credentials',
        'Map appointment types and calendars to Open agents',
        'Test a booking',
      ],
      authMethods: ['Acuity API user/key', 'OAuth 2.0 (developer plans)'],
      syncModes: { fullRefresh: true, incremental: true },
      requirements: [
        'Acuity Scheduling subscription (Emerging or higher for API)',
      ],
      notes: [
        'Squarespace Scheduling (rebrand) uses the same API',
        'Group classes and packages supported',
      ],
    },
    // Front
    {
      slug: 'front',
      name: 'Front',
      category: 'crm' as const,
      description:
        'Front integration — AI handles inboxes, tags conversations, drafts replies.',
      longDescription:
        "Connect Front (the shared-inbox platform) so the AI can read and reply across email, SMS, WhatsApp, and chat conversations under your existing Front workflows. Drafts replies for human approval, sends directly when confidence is high, applies Front tags consistently, and updates Front's analytics.",
      icon: '✉️',
      logoUrl: 'https://cdn.simpleicons.org/databricks/A53AFF',
      color: '#A53AFF',
      features: [
        {
          title: 'Multi-channel inboxes',
          description:
            'Email, SMS, WhatsApp, chat — all under Front, all handled by the AI.',
        },
        {
          title: 'Draft or send',
          description:
            'Per-rule or per-confidence-threshold draft vs send behaviour.',
        },
        {
          title: 'Tag consistency',
          description:
            'Front tags applied consistently for routing and reporting.',
        },
        {
          title: 'Workflow-aware',
          description: 'Existing Front rules and sequences continue to fire.',
        },
      ],
      syncCapabilities: [
        { name: 'Conversations', description: 'Read, reply, draft.' },
        { name: 'Inboxes', description: 'Per-inbox agent routing.' },
        { name: 'Tags', description: 'AI applies Front tags by intent.' },
        {
          name: 'Contacts',
          description: 'Linked to your CRM via Front contact records.',
        },
      ],
      useCases: [
        'Shared-inbox teams using Front for omnichannel customer service',
        'AI auto-reply on email and SMS with Front rules continuing to apply',
        'Tag-based routing driven by AI intent classification',
      ],
      setupSteps: [
        'In Front, create an API token with the required scopes',
        'In Open, go to Settings → Integrations → Front and provide the token',
        'Map inboxes to Open agents',
        'Configure draft vs send rules per inbox',
        'Test on a low-volume inbox before scaling',
      ],
      authMethods: ['API token', 'OAuth 2.0'],
      syncModes: { fullRefresh: true, incremental: true },
      requirements: ['Front Growth plan or higher with API access'],
      notes: [
        'Front Workflows continue to fire on AI-touched conversations',
        'Multi-team Front deployments supported',
      ],
    },
    // Help Scout
    {
      slug: 'help-scout',
      name: 'Help Scout',
      category: 'crm' as const,
      description:
        'Help Scout integration — AI handles conversations, applies workflows, drafts replies.',
      longDescription:
        "Connect Help Scout so the AI can read and reply to conversations, apply existing workflows, and tag/route consistently. Native Help Scout AI capabilities continue alongside Open's deeper agent layer.",
      icon: '💬',
      logoUrl: 'https://cdn.simpleicons.org/databricks/1292EE',
      color: '#1292EE',
      features: [
        {
          title: 'Conversation handling',
          description: 'Read, reply, draft on Help Scout conversations.',
        },
        {
          title: 'Mailbox routing',
          description: 'Per-mailbox agent assignment.',
        },
        {
          title: 'Workflow-aware',
          description:
            'Help Scout workflows continue to fire on AI-touched conversations.',
        },
        {
          title: 'Custom fields',
          description: 'AI fills custom fields during the conversation.',
        },
      ],
      syncCapabilities: [
        { name: 'Conversations', description: 'Read, reply, draft.' },
        { name: 'Mailboxes', description: 'Per-mailbox routing.' },
        { name: 'Customers', description: 'Help Scout customer records.' },
        { name: 'Tags', description: 'AI tag application.' },
      ],
      useCases: [
        'SMB and mid-market support teams on Help Scout',
        'AI auto-reply with confidence thresholds',
        'Mailbox-based routing for multi-product companies',
      ],
      setupSteps: [
        'In Help Scout, create an OAuth2 app or generate an API key',
        'In Open, go to Settings → Integrations → Help Scout and authorize',
        'Map mailboxes to agents',
        'Configure draft vs send rules',
        'Test on a low-volume mailbox',
      ],
      authMethods: ['OAuth 2.0', 'API key'],
      syncModes: { fullRefresh: true, incremental: true },
      requirements: ['Help Scout subscription with API access'],
      notes: [
        'Help Scout AI features continue alongside Open',
        'Workflows fire normally on AI-touched conversations',
      ],
    },
    // Kustomer
    {
      slug: 'kustomer',
      name: 'Kustomer',
      category: 'crm' as const,
      description:
        'Kustomer integration — AI handles conversations, fills KObjects, runs workflows.',
      longDescription:
        "Connect Kustomer so the AI can handle conversations across email, chat, and SMS, fill KObjects (Kustomer's flexible data model), and apply existing routing logic. Custom KObject types are inferred from your Kustomer schema automatically.",
      icon: '🟧',
      logoUrl: 'https://cdn.simpleicons.org/databricks/F26C4F',
      color: '#F26C4F',
      features: [
        {
          title: 'KObject-aware',
          description:
            'Reads your custom KObject schema and updates fields naturally.',
        },
        {
          title: 'Multi-channel',
          description: 'Email, chat, SMS conversations all handled.',
        },
        {
          title: 'Workflow integration',
          description:
            'Existing Kustomer workflows fire on AI-touched conversations.',
        },
        {
          title: 'Customer 360',
          description: "AI uses Kustomer's timeline view for full context.",
        },
      ],
      syncCapabilities: [
        { name: 'Conversations', description: 'Read, reply, draft.' },
        {
          name: 'Customers',
          description: 'Customer record lookup and update.',
        },
        {
          name: 'KObjects',
          description: 'Custom data model populated from conversation.',
        },
        { name: 'Teams', description: 'Per-team routing of escalations.' },
      ],
      useCases: [
        'Mid-market and enterprise support teams on Kustomer',
        'Custom-data-model deployments where AI fills KObjects',
        'Multi-channel deployments unified by Kustomer',
      ],
      setupSteps: [
        'In Kustomer, generate an API key with the required scopes',
        'In Open, go to Settings → Integrations → Kustomer and provide the key',
        'Map teams and queues to Open agents',
        'Test on a low-volume queue',
      ],
      authMethods: ['API key', 'OAuth 2.0'],
      syncModes: { fullRefresh: true, incremental: true },
      requirements: ['Kustomer subscription with API access'],
      notes: [
        'KObject schema introspected automatically',
        'Multi-org Kustomer deployments supported',
      ],
    },
    // Gorgias
    {
      slug: 'gorgias',
      name: 'Gorgias',
      category: 'crm' as const,
      description:
        'Gorgias integration — AI handles e-commerce support tickets with Shopify context.',
      longDescription:
        'Connect Gorgias so the AI handles e-commerce support tickets with full Shopify (or BigCommerce/Magento) context — order status, refunds, returns, exchanges. Gorgias macros continue to apply; AI replaces or augments them with adaptive responses.',
      icon: '🛒',
      logoUrl: 'https://cdn.simpleicons.org/databricks/000000',
      color: '#1F1F1F',
      features: [
        {
          title: 'E-commerce context',
          description:
            'Shopify, BigCommerce, Magento order data attached to every conversation.',
        },
        {
          title: 'Macro replacement',
          description:
            'AI selects and adapts macros, or replaces them with native generation.',
        },
        {
          title: 'Refund and return handling',
          description:
            'Configurable refund/return automation with policy bounds.',
        },
        {
          title: 'Multi-channel',
          description:
            'Email, chat, Instagram, Facebook Messenger, WhatsApp through Gorgias.',
        },
      ],
      syncCapabilities: [
        { name: 'Tickets', description: 'Read, reply, tag.' },
        {
          name: 'Customers',
          description: 'Customer record with order history.',
        },
        { name: 'Macros', description: 'AI uses or adapts existing macros.' },
        {
          name: 'Shopify orders',
          description: "Live order data via Gorgias's Shopify connection.",
        },
      ],
      useCases: [
        'E-commerce stores on Shopify + Gorgias',
        'Order-status, refund, and return automation',
        'Multi-channel e-commerce support unified by Gorgias',
      ],
      setupSteps: [
        'In Gorgias, generate an API key with the required scopes',
        'In Open, go to Settings → Integrations → Gorgias and provide credentials',
        'Map ticket views to agents',
        'Configure refund/return policy bounds',
        'Test on a single ticket queue',
      ],
      authMethods: ['API key + email', 'OAuth 2.0'],
      syncModes: { fullRefresh: true, incremental: true },
      requirements: [
        'Gorgias subscription',
        'Shopify, BigCommerce, or Magento connected to Gorgias',
      ],
      notes: [
        'Native Gorgias auto-respond features continue alongside Open',
        'Per-store agents supported for multi-store retailers',
      ],
    },
    // Microsoft Teams
    {
      slug: 'microsoft-teams',
      name: 'Microsoft Teams',
      category: 'it' as const,
      description:
        'Microsoft Teams integration — AI escalations, alerts, and Teams Phone routing.',
      longDescription:
        'Connect Microsoft Teams so AI agents can post escalation alerts to channels, ping individuals, and route inbound voice via Teams Phone (Direct Routing). Used together with Microsoft 365 Calendar for full Microsoft-shop deployments.',
      icon: '🟦',
      color: '#6264A7',
      features: [
        {
          title: 'Channel alerts',
          description:
            'AI posts escalation alerts to specified Teams channels with full context.',
        },
        {
          title: 'Direct messages',
          description:
            'Pages individual on-call humans via DM with the live transcript.',
        },
        {
          title: 'Teams Phone via Direct Routing',
          description:
            'AI answers Teams Phone calls when configured as a Direct Routing destination.',
        },
        {
          title: 'Adaptive cards',
          description:
            'Rich cards with action buttons for accept/decline/snooze on escalations.',
        },
      ],
      syncCapabilities: [
        { name: 'Channels', description: 'Post alerts, read context.' },
        { name: 'Direct messages', description: 'Page individuals on-call.' },
        {
          name: 'Teams Phone',
          description: 'Direct Routing inbound (enterprise).',
        },
        { name: 'Adaptive cards', description: 'Rich-card escalation flows.' },
      ],
      useCases: [
        'Enterprise on-call paging via Teams DMs',
        'Channel-based escalation visibility',
        'Microsoft-shop voice deployments via Teams Phone Direct Routing',
      ],
      setupSteps: [
        'In Microsoft Entra ID, register an Open app with Teams permissions',
        'In Open, go to Settings → Integrations → Microsoft Teams and authorize',
        'Map channels and individuals to escalation rules',
        'For Teams Phone: configure Direct Routing in your tenant',
        'Test alert and (if applicable) inbound call flow',
      ],
      authMethods: [
        'OAuth 2.0 delegated',
        'Application permissions (tenant-wide)',
      ],
      syncModes: { fullRefresh: false, incremental: true },
      requirements: [
        'Microsoft Teams enabled in your tenant',
        'Microsoft Entra ID admin to register the app',
        'Teams Phone with Direct Routing for inbound voice (optional)',
      ],
      notes: [
        'Sovereign clouds (GCC, GCC High, DoD) supported on enterprise plans',
        'Direct Routing certified SBC required for Teams Phone',
      ],
    },
    // Make
    {
      slug: 'make',
      name: 'Make',
      category: 'automation' as const,
      description:
        'Make integration — trigger Make scenarios from Open events, call Make webhooks.',
      longDescription:
        'Connect Make (formerly Integromat) so Open events (conversation start/end, escalation, intent detection, custom triggers) fire Make scenarios. Open also calls Make webhooks during conversations to invoke arbitrary scenarios — useful for connecting Open to long-tail systems Make supports natively.',
      icon: '🔗',
      logoUrl: 'https://cdn.simpleicons.org/make/6D00CC',
      color: '#6D00CC',
      features: [
        {
          title: 'Event-driven scenarios',
          description: 'Make scenarios trigger on Open events.',
        },
        {
          title: 'Webhook calls during conversation',
          description:
            'AI calls Make webhooks mid-call to run arbitrary logic.',
        },
        {
          title: '1500+ apps',
          description:
            "Reach Make's full app catalog from inside an Open conversation.",
        },
        {
          title: 'Bidirectional',
          description:
            "Make scenarios can also call Open's API to trigger outbound campaigns.",
        },
      ],
      syncCapabilities: [
        {
          name: 'Webhooks',
          description: 'Bidirectional Make ↔ Open webhooks.',
        },
        { name: 'Scenarios', description: 'Triggered by Open events.' },
        {
          name: 'Apps',
          description: "Make's 1500+ app catalog accessible from Open.",
        },
      ],
      useCases: [
        'Bridging Open to long-tail apps not natively integrated',
        'Custom workflows triggered by AI conversation outcomes',
        'Lightweight integration for SMB and mid-market without engineering bandwidth',
      ],
      setupSteps: [
        'In Make, create a scenario with a webhook trigger',
        'In Open, configure a tool that calls the Make webhook',
        'Test the bidirectional flow',
      ],
      authMethods: ['Make webhook URL', 'API key for outbound from Make'],
      syncModes: { fullRefresh: false, incremental: true },
      requirements: [
        'Make account (Free tier works for testing; paid for production volume)',
      ],
      notes: [
        "Useful when your stack has long-tail apps Open doesn't yet integrate natively",
        "Make scenarios can call back to Open's API",
      ],
    },
    // n8n
    {
      slug: 'n8n',
      name: 'n8n',
      category: 'automation' as const,
      description:
        'n8n integration — open-source automation triggered by Open events.',
      longDescription:
        'Connect n8n (open-source workflow automation) so Open events trigger n8n workflows and Open calls n8n webhooks during conversations. Self-hosted or cloud n8n both supported. Useful for engineering teams that prefer open-source / self-hosted automation.',
      icon: '🧩',
      logoUrl: 'https://cdn.simpleicons.org/n8n/EA4B71',
      color: '#EA4B71',
      features: [
        {
          title: 'Self-hosted or cloud',
          description:
            'n8n self-hosted in your infrastructure or n8n.cloud both supported.',
        },
        {
          title: 'Webhook bidirectional',
          description:
            "Open calls n8n webhooks mid-call; n8n calls Open's API.",
        },
        {
          title: 'Open-source automation',
          description:
            'Engineering teams that prefer open-source workflow tools get a first-class path.',
        },
        {
          title: 'Code nodes',
          description:
            'JavaScript and Python in n8n nodes lets you write custom logic Open can call.',
        },
      ],
      syncCapabilities: [
        { name: 'Webhooks', description: 'Bidirectional n8n ↔ Open.' },
        { name: 'Workflows', description: 'Triggered by Open events.' },
        {
          name: 'Code nodes',
          description: 'Custom logic accessible from Open.',
        },
      ],
      useCases: [
        'Engineering-led teams using n8n as their primary automation layer',
        'Self-hosted compliance-sensitive deployments',
        'Custom integrations with internal systems via n8n code nodes',
      ],
      setupSteps: [
        'In n8n, create a workflow with a Webhook trigger',
        'In Open, configure a tool that calls the n8n webhook',
        'For self-hosted n8n, ensure network reachability between Open and your n8n instance',
        'Test the bidirectional flow',
      ],
      authMethods: ['n8n webhook URL', 'Open API key for n8n → Open calls'],
      syncModes: { fullRefresh: false, incremental: true },
      requirements: ['n8n instance (self-hosted or n8n.cloud)'],
      notes: [
        'Self-hosted n8n is popular with EU compliance-sensitive deployments',
        'JavaScript and Python in code nodes supported',
      ],
    },
  ];
}

// Legacy export for backward compatibility (without translations)
export const TRAINING_INTEGRATIONS: IntegrationData[] = getAllIntegrations();

// Get integration FAQs with translation support
export function getIntegrationFAQs(
  slug: string,
  translations?: TranslationKeys['integrations'],
): FAQ[] {
  const tt = (key: string) => t(translations, key);

  const faqs: Record<string, FAQ[]> = {
    notion: [
      {
        question: tt('Do I need to share every page individually?'),
        answer: tt(
          'If using Access Token auth, yes - you must share each page with your integration. With OAuth 2.0, you can select pages during the authorization flow.',
        ),
      },
      {
        question: tt('How often does the content sync?'),
        answer: tt(
          'You can configure sync frequency in Open. Incremental sync only fetches changes since the last sync, making updates efficient.',
        ),
      },
      {
        question: tt('Are comments and discussions included?'),
        answer: tt(
          'Yes, if you enable the "Read comments" capability in your Notion integration settings.',
        ),
      },
    ],
    confluence: [
      {
        question: tt("Why can't I sync Spaces?"),
        answer: tt(
          'The Space stream requires a Confluence Standard or Premium plan. Free and Basic plans only support page sync.',
        ),
      },
      {
        question: tt('Does it support incremental sync?'),
        answer: tt(
          'No, Confluence currently only supports Full Refresh sync, meaning all content is re-synced each time.',
        ),
      },
    ],
    'google-docs': [
      {
        question: tt('Can I sync multiple folders?'),
        answer: tt(
          'The connector syncs from a single folder, but all subfolders are included recursively. For multiple unrelated folders, create separate connections.',
        ),
      },
      {
        question: tt('What file formats are supported?'),
        answer: tt(
          'CSV, JSON, JSONL, Parquet, Avro, Excel, and experimental document format for PDFs and Google Docs text extraction.',
        ),
      },
    ],
    'zendesk-help-center': [
      {
        question: tt("What's the difference between OAuth and API Token?"),
        answer: tt(
          "OAuth is simpler to set up and doesn't require managing tokens. API Token requires your Zendesk email and manually generated token.",
        ),
      },
      {
        question: tt('Are community forums included?'),
        answer: tt(
          'Yes, Posts and Topics from your Zendesk community are synced alongside Help Center articles.',
        ),
      },
    ],
    github: [
      {
        question: tt('Can I sync from multiple organizations?'),
        answer: tt(
          'Yes, list repositories from different orgs in the format "org1/repo1,org2/repo2" or use "org/*" for all repos in an org.',
        ),
      },
      {
        question: tt('How do I avoid rate limits?'),
        answer: tt(
          'Use multiple personal access tokens separated by commas to load balance API quota across tokens.',
        ),
      },
    ],
    slack: [
      {
        question: tt("Why aren't messages syncing from a channel?"),
        answer: tt(
          'The Slack app must be explicitly added to each channel. Go to the channel → Integrations → Add apps and select your app.',
        ),
      },
      {
        question: tt('Can I sync private channels?'),
        answer: tt(
          'Yes, but you must manually add the bot to private channels even if join_channels is enabled.',
        ),
      },
    ],
    'google-sheets': [
      {
        question: tt('Can I sync multiple spreadsheets?'),
        answer: tt(
          'Each connection syncs one spreadsheet. For multiple spreadsheets, create multiple connections or use Google Drive connector.',
        ),
      },
      {
        question: tt('How are formulas handled?'),
        answer: tt(
          'Formulas sync as their computed values, not the formula text itself.',
        ),
      },
    ],
    airtable: [
      {
        question: tt('What happens if I rename a table?'),
        answer: tt(
          'You need to reset the connection schema and re-select the renamed table. Airtable cannot notify Open of table renames.',
        ),
      },
      {
        question: tt('Are attachments synced?'),
        answer: tt(
          'Attachment URLs and metadata are synced. The actual file content requires additional processing.',
        ),
      },
    ],
    mongodb: [
      {
        question: tt('Do I need a replica set?'),
        answer: tt(
          'For CDC (incremental) sync, yes. Standalone MongoDB instances only support full refresh sync.',
        ),
      },
      {
        question: tt('What MongoDB versions are supported?'),
        answer: tt(
          'MongoDB 3.6 or higher is required for Change Streams support.',
        ),
      },
    ],
    sharepoint: [
      {
        question: tt('Does it work with on-premises SharePoint?'),
        answer: tt(
          'The connector is optimized for SharePoint Online (Microsoft 365). On-premises support may vary.',
        ),
      },
    ],
    snowflake: [
      {
        question: tt('What permissions does the user need?'),
        answer: tt(
          'SELECT permission on the tables/views you want to sync, and USAGE on the warehouse, database, and schema.',
        ),
      },
    ],
    bigquery: [
      {
        question: tt('Are there costs for syncing data?'),
        answer: tt(
          'Yes, BigQuery charges for data scanned during queries. Consider using views or partitioned tables to reduce costs.',
        ),
      },
    ],
  };

  return faqs[slug] || [];
}

export function getIntegrationBySlug(
  slug: string,
  translations?: TranslationKeys['integrations'],
): IntegrationData | undefined {
  return getAllIntegrations(translations).find(
    (integration) => integration.slug === slug,
  );
}

export function getAllIntegrationSlugs(): string[] {
  return TRAINING_INTEGRATIONS.map((integration) => integration.slug);
}
